import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  Users,
  MessageSquare,
  Shield,
  ChevronDown,
  ChevronRight,
  Eye,
  UserX,
  Crown,
  Database,
  Clock,
  AlertCircle,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  UserCheck,
  FileText,
  Code,
  Heart,
  ShoppingCart,
  HelpCircle,
  ToggleLeft,
  ToggleRight,
  Server,
  Star,
  ThumbsUp,
  ThumbsDown,
  CheckCircle,
  XCircle,
  RefreshCw,
  Package,
  Zap,
  Search,
  Download,
  Filter,
  ClipboardList,
  TrendingUp,
  BarChart2,
  ArrowUp,
  ArrowDown,
  BookOpen,
  Image,
  FileImage,
  Activity,
  Upload,
  DollarSign,
  KeyRound,
  Sparkles,
  Mail,
  Brain,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../layout/AppLayout';
import MobileHeader from '../layout/MobileHeader';
import VersionIndicator from '../components/common/VersionIndicator';
import LLMCostDashboard from './LLMCostDashboard';
import { adminService, AgentSkillGroup, AgentSkill, AlgorithmSetting } from '../services/AdminService';
import NotificationModal from '../components/common/modals/NotificationModal';
import ConfirmDialog from '../components/common/modals/ConfirmDialog';
import ReactMarkdown from 'react-markdown';
import JsonTreeViewer from '../components/common/JsonTreeViewer';
import { AutomationsTab, StateMachineTab } from './WorkflowsPage';
import { workflowService } from '../services/WorkflowService';
import KnowledgeRulesTab from './KnowledgeRulesTab';
import KnowledgeExamplesTab from './KnowledgeExamplesTab';
import RedisTab from './RedisTab';
import WmiTab from './WmiTab';
import MemoryTab from './MemoryTab';
import SharedCatalogsTab from './SharedCatalogsTab';
import BillingPlansTab from './BillingPlansTab';
import PlateLookupPanel from './PlateLookupPanel';
import { integrationService } from '../services/IntegrationService';
import GenericIntegrationSetupPage from './GenericIntegrationSetupPage';
import * as XLSX from 'xlsx';

interface AdminUser {
  id: number;
  uid: string;
  name: string;
  email: string;
  picture?: string;
  role: string;
  is_active: boolean;
  last_login?: number | null;
  session_count: number;
  organization_id?: number | null;
  organization_name?: string | null;
}

interface UserSession {
  id: string;
  session_id: string;
  user_id: string;
  user_name?: string;
  user_email?: string;
  user_picture?: string;
  last_update_time: number;
  app_name: string;
  state?: Record<string, any>;
  state_keys?: string[];
}

interface AgentInstruction {
  agent_name: string;
  folder: string;
  file_name: string;
  file_path: string;
  content?: string;
  lines?: number;
  size_bytes?: number;
  last_modified?: number;
  error?: string;
}

interface AgentInstructionsListResponse {
  status: string;
  total_agents: number;
  agents: AgentInstruction[];
  timestamp?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// OrgFeatureFlagsPanel — organization-scoped feature flags section
// ─────────────────────────────────────────────────────────────────────────────

const PRICING_FLAG = 'pricing_flow_wait_for_pricing';
const B2B_CUSTOMERS_FLAG = 'b2b_customers_enabled';
const DELIVERY_TIME_FLAG = 'delivery_time';
const ORDER_SLA_FLAG = 'order_sla';

const KNOWN_FLAGS = [
  { value: PRICING_FLAG, label: 'pricing_flow_wait_for_pricing', description: 'Cart: wait for pricing before submitting', badge: 'Cart Behavior', badgeClass: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: B2B_CUSTOMERS_FLAG, label: 'b2b_customers_enabled', description: 'Enable B2B Customers (CRM) feature for this organisation', badge: 'B2B / CRM', badgeClass: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: DELIVERY_TIME_FLAG, label: 'delivery_time', description: 'Show a delivery-time estimate on In Stock parts (spec §6)', badge: 'Cart Behavior', badgeClass: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: ORDER_SLA_FLAG, label: 'order_sla', description: 'Order lifecycle SLA nudges — Results Ready 4h, In Cart 24h, customer reminder 24h / escalation 48h. Off = orders stay quiet (handovers, retries and system errors still surface).', badge: 'Orders', badgeClass: 'bg-amber-50 text-[#8A5A00] border-amber-200' },
];

// Locales the welcome email template is translated into
// (catax_ai/channels/templates/welcome-tmp.translations.json).
const WELCOME_EMAIL_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'ja', label: '日本語' },
  { code: 'he', label: 'עברית' },
  { code: 'ar', label: 'العربية' },
];

// Top-level admin tabs that group several sub-tabs (nav id → its sub-tabs).
const SUB_TABS: Record<string, { id: string; label: string; icon: React.ElementType }[]> = {
  users: [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'organizations', label: 'Organizations', icon: Shield },
  ],
  workflows: [
    { id: 'workflows', label: 'Workflows', icon: Zap },
    { id: 'shared-integrations', label: 'Shared Integrations', icon: Database },
    { id: 'shared-catalogs', label: 'Shared Catalogs', icon: KeyRound },
  ],
  system: [
    { id: 'system', label: 'System', icon: Server },
    { id: 'redis', label: 'Redis', icon: Database },
    { id: 'wmi', label: 'WMI', icon: Database },
    { id: 'memory', label: 'Memory', icon: Brain },
    { id: 'messages', label: 'Messages', icon: AlertCircle },
  ],
};

const AlgorithmSettingsPanel: React.FC = () => {
  const [rows, setRows] = useState<AlgorithmSetting[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await adminService.getAlgorithmSettings();
      setRows(res.data.settings || []);
      setDrafts({});
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not load settings');
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (key: string, value: string | null) => {
    setBusy(key); setError(null);
    try {
      await adminService.updateAlgorithmSetting(key, value);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setBusy(null);
    }
  };

  const fmt = (v: any) => (v === null || v === undefined ? '—' : String(v));

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900 flex items-center">
          <Server className="w-5 h-5 mr-2" />
          Search Algorithm Settings
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Runtime knobs of the parts search and clarification logic. Precedence: admin override → environment
          variable → code default. Changes reach every server and worker within ~30 seconds, no restart.
        </p>
      </div>
      {error && <div className="mx-6 mt-4 p-3 bg-red-50 text-red-700 text-sm rounded">{error}</div>}
      <div className="p-6 space-y-3">
        {rows.map((r) => {
          const overridden = r.value !== null && r.value !== undefined;
          const draft = drafts[r.key];
          return (
            <div key={r.key} className={`border rounded-lg p-4 ${overridden ? 'border-blue-300 bg-blue-50/40' : 'border-gray-200'}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-medium text-gray-900">
                    {r.label} <code className="ml-1 text-xs text-gray-500">{r.key}</code>
                    {overridden && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">admin override</span>}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{r.help}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    effective <strong className="text-gray-700">{fmt(r.effective)}</strong>
                    {' · '}env {fmt(r.env_value)}{' · '}default {fmt(r.default)}
                    {r.min !== null && r.max !== null && ` · range ${r.min}–${r.max}`}
                    {r.updated_by && ` · set by ${r.updated_by}${r.updated_at ? ' at ' + new Date(r.updated_at).toLocaleString() : ''}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {r.type === 'bool' ? (
                    <button
                      onClick={() => save(r.key, r.effective ? 'false' : 'true')}
                      disabled={busy === r.key}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${r.effective ? 'bg-blue-600' : 'bg-gray-300'}`}
                      title={r.effective ? 'On — click to turn off' : 'Off — click to turn on'}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${r.effective ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  ) : (
                    <>
                      <input
                        type="number"
                        step={r.type === 'float' ? '0.01' : '1'}
                        min={r.min ?? undefined}
                        max={r.max ?? undefined}
                        value={draft ?? String(r.effective)}
                        onChange={(e) => setDrafts((d) => ({ ...d, [r.key]: e.target.value }))}
                        className="w-24 px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                      <button
                        onClick={() => save(r.key, draft ?? String(r.effective))}
                        disabled={busy === r.key || draft === undefined || draft === String(r.effective)}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        Save
                      </button>
                    </>
                  )}
                  {overridden && (
                    <button
                      onClick={() => save(r.key, null)}
                      disabled={busy === r.key}
                      className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                      title="Remove the admin override (back to env / default)"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {!rows.length && !error && <p className="text-sm text-gray-500">Loading…</p>}
      </div>
    </div>
  );
};

const OrgFeatureFlagsPanel: React.FC<{
  organizations: any[];
  onFetchOrgs: () => void;
  adminService: any;
  showNotification: (title: string, msg: string, type: 'success' | 'error' | 'warning' | 'info') => void;
}> = ({ organizations, onFetchOrgs, adminService, showNotification }) => {
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [orgFlags, setOrgFlags] = useState<any[]>([]);
  const [loadingFlags, setLoadingFlags] = useState(false);
  const [showAddFlag, setShowAddFlag] = useState(false);
  const [newFlagName, setNewFlagName] = useState('');
  const [newFlagEnabled, setNewFlagEnabled] = useState(false);
  const [addingFlag, setAddingFlag] = useState(false);

  const loadOrgFlags = async (orgId: number) => {
    setLoadingFlags(true);
    try {
      const res = await adminService.getOrgFeatureFlags(orgId);
      const loaded: any[] = res.data.flags || [];
      // Merge: ensure all KNOWN_FLAGS are always shown, defaulting to OFF if not in DB
      const loadedMap = new Map(loaded.map((f: any) => [f.flag_name, f]));
      const merged = [
        ...KNOWN_FLAGS.map(kf => loadedMap.get(kf.value) ?? { flag_name: kf.value, is_enabled: (kf as any).defaultEnabled ?? false }),
        ...loaded.filter((f: any) => !KNOWN_FLAGS.some(kf => kf.value === f.flag_name)),
      ];
      setOrgFlags(merged);
    } catch {
      showNotification('Error', 'Failed to load org feature flags', 'error');
    } finally {
      setLoadingFlags(false);
    }
  };

  const handleOrgChange = (orgId: number | null) => {
    setSelectedOrgId(orgId);
    setOrgFlags([]);
    setShowAddFlag(false);
    if (orgId) loadOrgFlags(orgId);
  };

  const handleToggle = async (flag: any) => {
    if (!selectedOrgId) return;
    try {
      await adminService.updateOrgFeatureFlag(selectedOrgId, flag.flag_name, !flag.is_enabled);
      showNotification('Success', `${flag.flag_name} ${!flag.is_enabled ? 'enabled' : 'disabled'} for this org`, 'success');
      loadOrgFlags(selectedOrgId);
    } catch {
      showNotification('Error', 'Failed to update flag', 'error');
    }
  };

  const handleAddFlag = async () => {
    if (!selectedOrgId || !newFlagName.trim()) return;
    setAddingFlag(true);
    try {
      await adminService.updateOrgFeatureFlag(selectedOrgId, newFlagName.trim(), newFlagEnabled);
      showNotification('Success', `Flag "${newFlagName.trim()}" added`, 'success');
      setShowAddFlag(false);
      setNewFlagName('');
      setNewFlagEnabled(false);
      loadOrgFlags(selectedOrgId);
    } catch {
      showNotification('Error', 'Failed to add flag', 'error');
    } finally {
      setAddingFlag(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-500" />
            Organization-Scoped Feature Flags
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">Per-organization overrides. Select an organization to manage its flags.</p>
        </div>
        {selectedOrgId && (
          <button
            onClick={() => { setShowAddFlag(v => !v); setNewFlagName(''); setNewFlagEnabled(false); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Flag
          </button>
        )}
      </div>

      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
        <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
        <div className="flex items-center gap-3">
          <select
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            value={selectedOrgId ?? ''}
            onClick={onFetchOrgs}
            onChange={e => handleOrgChange(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— select an organisation —</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
          {selectedOrgId && (
            <button
              onClick={() => loadOrgFlags(selectedOrgId)}
              className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Add Flag Form */}
      {selectedOrgId && showAddFlag && (
        <div className="px-6 py-4 border-b border-purple-100 bg-purple-50">
          <h3 className="text-sm font-medium text-gray-800 mb-3">New Flag</h3>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Flag Name</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  list="known-flags-list"
                  placeholder="e.g. pricing_flow_wait_for_pricing"
                  value={newFlagName}
                  onChange={e => setNewFlagName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
                <datalist id="known-flags-list">
                  {KNOWN_FLAGS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </datalist>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Initial State</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setNewFlagEnabled(v => !v)}
                  className="flex items-center gap-1 text-sm"
                >
                  {newFlagEnabled
                    ? <ToggleRight className="w-8 h-8 text-green-500" />
                    : <ToggleLeft className="w-8 h-8 text-gray-300" />}
                  <span className={newFlagEnabled ? 'text-green-700 font-medium' : 'text-gray-500'}>
                    {newFlagEnabled ? 'ON' : 'OFF'}
                  </span>
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddFlag}
                disabled={addingFlag || !newFlagName.trim()}
                className="px-4 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {addingFlag && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Save
              </button>
              <button
                onClick={() => setShowAddFlag(false)}
                className="px-4 py-1.5 text-sm border border-gray-300 text-gray-600 rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedOrgId && (
        loadingFlags ? (
          <div className="py-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto" />
            <p className="mt-2 text-sm text-gray-500">Loading...</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {orgFlags.map((flag, idx) => {
              const knownMeta = KNOWN_FLAGS.find(kf => kf.value === flag.flag_name);
              return (
              <div key={idx} className="px-6 py-4 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-sm font-medium text-gray-900">{flag.flag_name}</h3>
                    {knownMeta?.badge && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${knownMeta.badgeClass}`}>
                        {knownMeta.badge}
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${flag.is_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                      {flag.is_enabled ? 'ON' : 'OFF'}
                    </span>
                  </div>
                  {(knownMeta?.description || flag.description) && (
                    <p className="mt-0.5 text-xs text-gray-500">{knownMeta?.description ?? flag.description}</p>
                  )}
                  {flag.updated_by && (
                    <p className="mt-0.5 text-xs text-gray-400">
                      Updated by {flag.updated_by}{flag.updated_at ? ` · ${new Date(flag.updated_at).toLocaleString()}` : ''}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleToggle(flag)}
                  className="ml-4 flex-shrink-0"
                  title={flag.is_enabled ? 'Disable' : 'Enable'}
                >
                  {flag.is_enabled
                    ? <ToggleRight className="w-10 h-10 text-green-500 hover:text-green-700 transition-colors" />
                    : <ToggleLeft className="w-10 h-10 text-gray-300 hover:text-gray-500 transition-colors" />}
                </button>
              </div>
              );
            })}
            {orgFlags.length === 0 && (
              <div className="px-6 py-8 text-center text-gray-400">
                <ToggleLeft className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No flags configured. Click <strong>Add Flag</strong> to create one.</p>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const AdminPage: React.FC = () => {
  const { currentUser, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { tab } = useParams<{ tab?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'users' | 'organizations' | 'feedback' | 'agents' | 'sessions' | 'workflows' | 'feature-flags' | 'dashboard' | 'messages' | 'shared-integrations' | 'shared-catalogs' | 'billing-plans' | 'knowledge' | 'knowledge-examples' | 'system' | 'llm-costs' | 'redis' | 'wmi' | 'memory'>('users');
  const [triggeringJob, setTriggeringJob] = useState<string | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [agentInstructions, setAgentInstructions] = useState<AgentInstruction[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentInstruction | null>(null);
  // Skills state
  const [agentsSubTab, setAgentsSubTab] = useState<'instructions' | 'skills'>('instructions');
  const [agentSkills, setAgentSkills] = useState<AgentSkillGroup[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<(AgentSkill & { agent_folder: string }) | null>(null);
  const [editingSkillContent, setEditingSkillContent] = useState<string>('');
  const [skillEditMode, setSkillEditMode] = useState<'view' | 'edit'>('view');
  const [skillSaving, setSkillSaving] = useState(false);
  const [expandedSkillAgents, setExpandedSkillAgents] = useState<Set<string>>(new Set());
  // Instructions edit state
  const [editingInstructionContent, setEditingInstructionContent] = useState<string>('');
  const [instructionEditMode, setInstructionEditMode] = useState<'view' | 'edit'>('view');
  const [instructionSaving, setInstructionSaving] = useState(false);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [billingPlans, setBillingPlans] = useState<Array<{ id: number; name: string; is_active: boolean }>>([]);
  const [selectedOrg, setSelectedOrg] = useState<any | null>(null);
  const [orgMembers, setOrgMembers] = useState<any[]>([]);
  const [systemHealth, setSystemHealth] = useState<any | null>(null);
  const [feedbackSummary, setFeedbackSummary] = useState<any | null>(null);
  const [recentFeedback, setRecentFeedback] = useState<any[]>([]);
  const [allOrders, setAllOrders] = useState<any[]>([]);
  const [ordersStats, setOrdersStats] = useState<any>(null);
  const [assistanceRequests, setAssistanceRequests] = useState<any[]>([]);
  const [assistanceStats, setAssistanceStats] = useState<any>(null);
  const [featureFlags, setFeatureFlags] = useState<any[]>([]);
  // System Messages
  const [systemMessages, setSystemMessages] = useState<any[]>([]);
  const [systemMessagesLoading, setSystemMessagesLoading] = useState(false);
  const [showCreateMessage, setShowCreateMessage] = useState(false);
  const [editingMessage, setEditingMessage] = useState<any | null>(null);
  const [messageForm, setMessageForm] = useState({
    title: '',
    message: '',
    message_type: 'info',
    is_active: true,
    organization_id: null as number | null,
  });
  const [messageSaving, setMessageSaving] = useState(false);
  const [auditEntries, setAuditEntries] = useState<any[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [assistanceLoading, setAssistanceLoading] = useState(false);
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  // Parts Operations Dashboard state
  const [dashboardOrgId, setDashboardOrgId] = useState<number | null>(null);
  const [dashboardStartDate, setDashboardStartDate] = useState<string>('');
  const [dashboardEndDate, setDashboardEndDate] = useState<string>('');
  const [dashboardAssumedPrice, setDashboardAssumedPrice] = useState<number>(250);
  const [dashboardData, setDashboardData] = useState<any | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Search & filter state — synced with URL query params
  const userSearchQuery = searchParams.get('search') ?? '';
  const userRoleFilter = searchParams.get('role') ?? '';
  const userActiveFilter = searchParams.get('status') ?? '';
  const userOrgFilter = searchParams.get('org') ?? '';
  // Users pagination — synced with URL query params
  const USERS_PAGE_SIZE = 20;
  const usersPage = parseInt(searchParams.get('page') ?? '0', 10);

  const setUserSearchQuery = (v: string) => setSearchParams(p => { const n = new URLSearchParams(p); v ? n.set('search', v) : n.delete('search'); n.set('page', '0'); return n; }, { replace: true });
  const setUserRoleFilter = (v: string) => setSearchParams(p => { const n = new URLSearchParams(p); v ? n.set('role', v) : n.delete('role'); n.set('page', '0'); return n; }, { replace: true });
  const setUserActiveFilter = (v: string) => setSearchParams(p => { const n = new URLSearchParams(p); v ? n.set('status', v) : n.delete('status'); n.set('page', '0'); return n; }, { replace: true });
  const setUserOrgFilter = (v: string) => setSearchParams(p => { const n = new URLSearchParams(p); v ? n.set('org', v) : n.delete('org'); n.set('page', '0'); return n; }, { replace: true });
  const setUsersPage = (pg: number) => setSearchParams(p => { const n = new URLSearchParams(p); n.set('page', String(pg)); return n; }, { replace: true });
  const [usersTotal, setUsersTotal] = useState(0);
  // Local draft for the search input — only committed to URL on Enter / Search button
  const [userSearchDraft, setUserSearchDraft] = useState(userSearchQuery);
  // Edit user modal
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [sendingWelcomeEmailUid, setSendingWelcomeEmailUid] = useState<string | null>(null);
  const [welcomeEmailUser, setWelcomeEmailUser] = useState<AdminUser | null>(null);
  const [welcomeEmailPassword, setWelcomeEmailPassword] = useState('');
  const [welcomeEmailLocale, setWelcomeEmailLocale] = useState('en');
  const [editUserForm, setEditUserForm] = useState({ name: '', email: '', role: 'user', organization_id: null as number | null });
  const [editUserSaving, setEditUserSaving] = useState(false);
  const [sessionUserFilter, setSessionUserFilter] = useState('');
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsOffset, setSessionsOffset] = useState(0);
  const [sessionsHasMore, setSessionsHasMore] = useState(false);
  const [sessionSortOrder, setSessionSortOrder] = useState<'newest' | 'oldest'>('newest');
  // Bulk selection state
  const [selectedUserUids, setSelectedUserUids] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [expandedOrgs, setExpandedOrgs] = useState<Set<number>>(new Set());
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrgData, setNewOrgData] = useState({ name: '', description: '' });
  const [editingOrg, setEditingOrg] = useState<any | null>(null);
  const [addMemberEmail, setAddMemberEmail] = useState('');
  const [addMemberRole, setAddMemberRole] = useState('member');
  const [orgSettingsMap, setOrgSettingsMap] = useState<Record<number, { alias?: string; language: string; currency: string; country?: string | null; mission_critical: boolean; external_enrichment: boolean; daily_api_call_limit: number | null; api_calls_today?: number | null; plan_id: string | null; usage_enforcement: string; usage_month?: { orders: number; part_queries: number; llm_queries: number; quotes?: number; images?: number; pdfs?: number } | null; billing_plan_id: number | null; billing_plan_name?: string | null; quote_monthly_price_cents: number | null; quote_included_monthly: number | null; quote_overage_price_cents: number | null; quote_allowance_searches: number | null; quote_allowance_images: number | null; quote_allowance_pdfs: number | null; roi_calculator_enabled: boolean; roi_b2b_enabled: boolean; roi_b2c_enabled: boolean; roi_monthly_quotes: number | null; roi_fte_multiplier: number | null; roi_currency: string | null; roi_fte_cost: number | null; roi_cost_per_quote: number | null; roi_b2c_quotes: number | null; roi_b2c_cr: number | null; roi_b2c_aov: number | null; roi_b2c_gm: number | null; roi_b2c_cpq: number | null; roi_b2c_pct: number | null; roi_b2c_pricing_mode: string | null }>>({});
  const [orgSettingsSaving, setOrgSettingsSaving] = useState<Set<number>>(new Set());

  // Workflows tab state
  const [workflowsScope, setWorkflowsScope] = useState<'shared' | 'org'>('shared');
  const [workflowsOrgId, setWorkflowsOrgId] = useState<number | null>(null);
  const [workflowsStateConfig, setWorkflowsStateConfig] = useState<any | null>(null);
  const [workflowsStateLoading, setWorkflowsStateLoading] = useState(false);
  const [workflowsSharedConfig, setWorkflowsSharedConfig] = useState<any | null>(null);
  const [workflowsSharedLoading, setWorkflowsSharedLoading] = useState(false);

  // Shared Integrations tab state
  const [sharedSources, setSharedSources] = useState<any[]>([]);
  const [sharedSourcesLoading, setSharedSourcesLoading] = useState(false);
  const [showCreateSharedSource, setShowCreateSharedSource] = useState(false);
  const [createStep, setCreateStep] = useState<1 | 2 | 3>(1);
  const [newSharedSource, setNewSharedSource] = useState({
    name: '',
    integration_type: '',
    source_type: 'csv',
    apiUrl: '',
    apiKey: '',
    websiteUrl: '',
    username: '',
    password: '',
    shared_organization_ids: [] as number[],
  });
  const [editingSharedSource, setEditingSharedSource] = useState<any | null>(null);
  const [sharedSourceSaving, setSharedSourceSaving] = useState(false);
  // CSV upload state for shared source wizard
  const [sharedCsvFile, setSharedCsvFile] = useState<File | null>(null);
  const [sharedCsvHeaders, setSharedCsvHeaders] = useState<string[]>([]);
  const [sharedCsvMapping, setSharedCsvMapping] = useState<Record<string, string>>({});
  const [sharedParsingFile, setSharedParsingFile] = useState(false);
  // Dynamic mapping fields – user can add/remove rows
  const defaultMappingFields = [
    { id: 'oe_column', label: 'OE Number', required: true },
    { id: 'price_column', label: 'Price', required: false },
    { id: 'currency_column', label: 'Currency', required: false },
    { id: 'catalog_name_column', label: 'Catalog Name', required: false },
    { id: 'internal_catalog_number_column', label: 'Internal Catalog #', required: false },
    { id: 'supplier_part_number_column', label: 'Supplier Part #', required: false },
    { id: 'description_column', label: 'Description', required: false },
    { id: 'unit_column', label: 'Unit', required: false },
  ];
  const [sharedMappingFields, setSharedMappingFields] = useState<{ id: string; label: string; required: boolean }[]>(defaultMappingFields);

  // Add User modal state
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [newUserData, setNewUserData] = useState({
    name: '',
    middle_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'user',
    send_welcome_email: true,
    welcome_email_locale: 'en',
    organization_id: null as number | null,
    organization_role: 'member'
  });

  // Notification modal state
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const showNotification = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setNotification({ isOpen: true, title, message, type });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  const handleRunJob = async (jobKey: string, label: string) => {
    setTriggeringJob(jobKey);
    try {
      const res = await adminService.runCloudRunJob(jobKey);
      showNotification('Job triggered', res.data.message || `${label} started`, 'success');
    } catch (err: any) {
      const detail = err?.response?.data?.detail || `Failed to trigger ${label}`;
      showNotification('Error', detail, 'error');
    } finally {
      setTriggeringJob(null);
    }
  };

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
    confirmText?: string;
    cancelText?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    variant: 'danger',
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => void,
    variant: 'danger' | 'warning' | 'info' = 'warning',
    confirmText: string = 'Confirm',
    cancelText: string = 'Cancel'
  ) => {
    setConfirmDialog({ isOpen: true, title, message, onConfirm, variant, confirmText, cancelText });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  const fetchUsers = async (page = usersPage, search = userSearchQuery, role = userRoleFilter, status = userActiveFilter, org = userOrgFilter) => {
    setUsersLoading(true);
    try {
      const response = await adminService.getUsers({
        limit: USERS_PAGE_SIZE,
        offset: page * USERS_PAGE_SIZE,
        search: search || undefined,
        role: role || undefined,
        status: status || undefined,
        organization_id: org ? Number(org) : undefined,
      });
      setUsers(response.data.items);
      setUsersTotal(response.data.total);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchSessions = async (userFilter?: string, offset: number = 0) => {
    // The admin session browser is retired because listing sessions across all
    // users fans out to the session store and can exhaust the request timeout.
    setSessions([]);
    setSessionsTotal(0);
    setSessionsOffset(offset);
    setSessionsHasMore(false);
  };

  const fetchAgentInstructions = async () => {
    setAgentsLoading(true);
    try {
      const response = await adminService.getAgentInstructions();
      setAgentInstructions(response.data?.agents ?? []);
    } catch (err) {
      console.error('Failed to fetch agent instructions:', err);
      setAgentInstructions([]);
    } finally {
      setAgentsLoading(false);
    }
  };

  const fetchAgentSkills = async () => {
    setSkillsLoading(true);
    try {
      const response = await adminService.getAgentSkills();
      const agents = response.data?.agents ?? [];
      setAgentSkills(agents);
      // Auto-expand all agent sections
      setExpandedSkillAgents(new Set(agents.map((a: any) => a.agent_folder)));
    } catch (err) {
      console.error('Failed to fetch agent skills:', err);
      setAgentSkills([]);
    } finally {
      setSkillsLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    setOrganizationsLoading(true);
    try {
      const response = await adminService.getOrganizations();
      setOrganizations(response.data.organizations);
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    } finally {
      setOrganizationsLoading(false);
    }
  };

  const fetchBillingPlans = async () => {
    try {
      const response = await adminService.listBillingPlans();
      setBillingPlans(response.data?.plans || []);
    } catch (err) {
      // Backend may predate billing plans — dropdown just stays empty.
      console.error('Failed to fetch billing plans:', err);
    }
  };

  const fetchSystemHealth = async () => {
    setHealthLoading(true);
    try {
      const response = await adminService.getSystemHealth();
      setSystemHealth(response.data);
    } catch (err) {
      console.error('Failed to fetch system health:', err);
    } finally {
      setHealthLoading(false);
    }
  };

  const fetchFeedback = async () => {
    setFeedbackLoading(true);
    try {
      const [summaryRes, recentRes] = await Promise.all([
        adminService.getFeedbackSummary(),
        adminService.getRecentFeedback(20)
      ]);
      setFeedbackSummary(summaryRes.data);
      setRecentFeedback(recentRes.data.feedback || []);
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const fetchAllOrders = async () => {
    setOrdersLoading(true);
    try {
      const response = await adminService.getAllOrders(50, 0);
      setAllOrders(response.data.orders || []);
      setOrdersStats(response.data.status_distribution || {});
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchAssistanceRequests = async () => {
    setAssistanceLoading(true);
    try {
      const response = await adminService.getAssistanceRequests(50, 0);
      setAssistanceRequests(response.data.requests || []);
      setAssistanceStats({
        status_distribution: response.data.status_distribution || {},
        priority_distribution: response.data.priority_distribution || {},
        total_count: response.data.total_count || 0
      });
    } catch (err) {
      console.error('Failed to fetch assistance requests:', err);
    } finally {
      setAssistanceLoading(false);
    }
  };

  const fetchFeatureFlags = async () => {
    setFlagsLoading(true);
    try {
      const response = await adminService.getFeatureFlags();
      setFeatureFlags(response.data.flags || []);
    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
    } finally {
      setFlagsLoading(false);
    }
  };

  const fetchSystemMessages = async () => {
    setSystemMessagesLoading(true);
    try {
      const response = await adminService.getSystemMessages();
      setSystemMessages(response.data.messages || []);
    } catch (err) {
      console.error('Failed to fetch system messages:', err);
    } finally {
      setSystemMessagesLoading(false);
    }
  };

  const handleSaveMessage = async () => {
    if (!messageForm.title.trim() || !messageForm.message.trim()) return;
    setMessageSaving(true);
    try {
      if (editingMessage) {
        await adminService.updateSystemMessage(editingMessage.id, messageForm);
        showNotification('Success', 'Message updated', 'success');
      } else {
        await adminService.createSystemMessage(messageForm);
        showNotification('Success', 'Message created', 'success');
      }
      setShowCreateMessage(false);
      setEditingMessage(null);
      setMessageForm({ title: '', message: '', message_type: 'info', is_active: true, organization_id: null });
      fetchSystemMessages();
    } catch (err) {
      showNotification('Error', 'Failed to save message', 'error');
    } finally {
      setMessageSaving(false);
    }
  };

  const handleDeleteMessage = (msg: any) => {
    showConfirmDialog(
      'Delete Message',
      `Delete "${msg.title}"? It will no longer be shown to users.`,
      async () => {
        try {
          await adminService.deleteSystemMessage(msg.id);
          showNotification('Success', 'Message deleted', 'success');
          fetchSystemMessages();
        } catch {
          showNotification('Error', 'Failed to delete message', 'error');
        }
      },
      'danger', 'Delete', 'Cancel'
    );
  };

  const handleToggleMessage = async (msg: any) => {
    try {
      await adminService.updateSystemMessage(msg.id, { is_active: !msg.is_active });
      showNotification('Success', `Message ${msg.is_active ? 'deactivated' : 'activated'}`, 'success');
      fetchSystemMessages();
    } catch {
      showNotification('Error', 'Failed to toggle message', 'error');
    }
  };

  const openEditMessage = (msg: any) => {
    setEditingMessage(msg);
    setMessageForm({
      title: msg.title,
      message: msg.message,
      message_type: msg.message_type,
      is_active: msg.is_active,
      organization_id: msg.organization_id ?? null,
    });
    setShowCreateMessage(true);
  };

  const fetchOrgDashboard = async (orgId: number, startDate?: string, endDate?: string, assumedPrice?: number) => {
    if (!orgId) return;
    setDashboardLoading(true);
    try {
      const response = await adminService.getOrgDashboard({
        org_id: orgId,
        start_date: startDate || dashboardStartDate || undefined,
        end_date: endDate || dashboardEndDate || undefined,
        assumed_price: assumedPrice ?? dashboardAssumedPrice,
      });
      setDashboardData(response.data);
    } catch (err) {
      console.error('Failed to fetch org dashboard:', err);
      showNotification('Error', 'Failed to load dashboard data', 'error');
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    // Set active tab based on URL parameter
    if (tab && ['users', 'organizations', 'feedback', 'agents', 'workflows', 'feature-flags', 'dashboard', 'messages', 'shared-integrations', 'shared-catalogs', 'billing-plans', 'knowledge', 'knowledge-examples', 'system', 'llm-costs', 'redis', 'wmi', 'memory'].includes(tab)) {
      setActiveTab(tab as any);
    } else {
      // Default unknown and removed tabs to users.
      navigate('/admin/users', { replace: true });
    }

    // Check if we should switch to a specific tab from navigation state
    const state = location.state as any;
    if (state?.activeTab) {
      setActiveTab(state.activeTab);
      navigate(`/admin/${state.activeTab}`, { replace: true });
    }
  }, [tab, location.state, navigate]);

  // Re-fetch skills if user switches to the skills sub-tab and data is not yet loaded
  useEffect(() => {
    if (activeTab === 'agents' && agentsSubTab === 'skills' && agentSkills.length === 0 && !skillsLoading) {
      fetchAgentSkills();
    }
  }, [agentsSubTab, activeTab]);

  // Keep search draft in sync when URL changes externally (browser back/forward)
  useEffect(() => {
    setUserSearchDraft(searchParams.get('search') ?? '');
  }, [searchParams.get('search')]);

  // Load data when activeTab changes
  useEffect(() => {
    const loadTabData = async () => {
      try {
        setLoading(true);
        setError(null);

        switch (activeTab) {
          case 'users':
            // Organizations power the org filter dropdown on this tab.
            await Promise.all([fetchUsers(), fetchOrganizations()]);
            break;
          case 'organizations':
            // Billing plans power the plan-assignment dropdown per org.
            await Promise.all([fetchOrganizations(), fetchBillingPlans()]);
            break;
          case 'feedback':
            await fetchFeedback();
            break;
          case 'workflows':
            await fetchOrganizations();
            break;
          case 'shared-integrations':
            await fetchOrganizations();
            break;
          case 'shared-catalogs':
            await fetchOrganizations();
            break;
          case 'feature-flags':
            await Promise.all([fetchFeatureFlags(), fetchOrganizations()]);
            break;
          case 'messages':
            await Promise.all([fetchSystemMessages(), fetchOrganizations()]);
            break;
          case 'dashboard':
            await fetchOrganizations();
            break;
        }
      } catch (err: any) {
        console.error('Failed to fetch admin data:', err);
        if (err.response?.status === 403) {
          setError('Access denied. Admin privileges required.');
        } else if (err.response?.status === 401) {
          setError('Authentication required. Please log in again.');
          navigate('/login');
        } else {
          setError('Failed to load admin data.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadTabData();
  }, [activeTab, navigate]);

  const fetchOrgMembers = async (orgId: number) => {
    try {
      const response = await adminService.getOrganizationMembers(orgId);
      setOrgMembers(response.data.members);
    } catch (err) {
      console.error('Failed to fetch org members:', err);
      showNotification('Error', 'Failed to fetch organization members', 'error');
    }
  };

  const handleCreateOrganization = async () => {
    try {
      await adminService.createOrganization(newOrgData.name, newOrgData.description);
      setShowCreateOrg(false);
      setNewOrgData({ name: '', description: '' });
      // Refresh organizations data
      try {
        setOrganizationsLoading(true);
        const response = await adminService.getOrganizations();
        setOrganizations(response.data.organizations);
      } catch (err) {
        console.error('Failed to fetch organizations:', err);
        throw err;
      } finally {
        setOrganizationsLoading(false);
      }
      showNotification('Success', 'Organization created successfully', 'success');
    } catch (err: any) {
      console.error('Failed to create organization:', err);
      showNotification('Error', err.response?.data?.detail || 'Failed to create organization', 'error');
    }
  };

  const handleUpdateOrganization = async (orgId: number, updates: any) => {
    // Validate alias uniqueness before saving
    if (updates.alias) {
      const conflict = organizations.find(o => o.id !== orgId && o.alias === updates.alias);
      if (conflict) {
        showNotification('Error', `Alias "${updates.alias}" is already used by organization "${conflict.name}". Please choose a different alias.`, 'error');
        return;
      }
    }
    try {
      await adminService.updateOrganization(orgId, updates);
      // Refresh organizations data
      try {
        setOrganizationsLoading(true);
        const response = await adminService.getOrganizations();
        setOrganizations(response.data.organizations);
      } catch (err) {
        console.error('Failed to fetch organizations:', err);
        throw err;
      } finally {
        setOrganizationsLoading(false);
      }
      setEditingOrg(null);
      showNotification('Success', 'Organization updated successfully', 'success');
    } catch (err: any) {
      console.error('Failed to update organization:', err);
      showNotification('Error', err.response?.data?.detail || 'Failed to update organization', 'error');
    }
  };

  const handleDeleteOrganization = async (orgId: number, orgName: string) => {
    showConfirmDialog(
      'Delete Organization',
      `Are you sure you want to delete "${orgName}"? This will remove all members and settings. This action cannot be undone.`,
      async () => {
        try {
          await adminService.deleteOrganization(orgId);
          // Refresh organizations data
          try {
            setOrganizationsLoading(true);
            const response = await adminService.getOrganizations();
            setOrganizations(response.data.organizations);
          } catch (err) {
            console.error('Failed to fetch organizations:', err);
            throw err;
          } finally {
            setOrganizationsLoading(false);
          }
          setSelectedOrg(null);
          showNotification('Success', 'Organization deleted successfully', 'success');
        } catch (err: any) {
          console.error('Failed to delete organization:', err);
          showNotification('Error', err.response?.data?.detail || 'Failed to delete organization', 'error');
        }
      },
      'danger',
      'Delete',
      'Cancel'
    );
  };

  const handleAddMemberToOrg = async (orgId: number) => {
    try {
      await adminService.addOrganizationMember(orgId, addMemberEmail, addMemberRole);
      setAddMemberEmail('');
      setAddMemberRole('member');
      await fetchOrgMembers(orgId);
      showNotification('Success', 'Member added successfully', 'success');
    } catch (err: any) {
      console.error('Failed to add member:', err);
      showNotification('Error', err.response?.data?.detail || 'Failed to add member', 'error');
    }
  };

  const handleRemoveMemberFromOrg = async (orgId: number, userUid: string, userName: string) => {
    showConfirmDialog(
      'Remove Member',
      `Are you sure you want to remove "${userName}" from this organization?`,
      async () => {
        try {
          await adminService.removeOrganizationMember(orgId, userUid);
          await fetchOrgMembers(orgId);
          showNotification('Success', 'Member removed successfully', 'success');
        } catch (err: any) {
          console.error('Failed to remove member:', err);
          showNotification('Error', err.response?.data?.detail || 'Failed to remove member', 'error');
        }
      },
      'warning',
      'Remove',
      'Cancel'
    );
  };

  const handleUpdateMemberRole = async (orgId: number, userUid: string, newRole: string) => {
    try {
      await adminService.updateMemberRole(orgId, userUid, newRole);
      await fetchOrgMembers(orgId);
      showNotification('Success', 'Member role updated successfully', 'success');
    } catch (err: any) {
      console.error('Failed to update member role:', err);
      showNotification('Error', err.response?.data?.detail || 'Failed to update member role', 'error');
    }
  };

  const toggleOrgExpansion = async (orgId: number) => {
    setExpandedOrgs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orgId)) {
        newSet.delete(orgId);
      } else {
        newSet.add(orgId);
        // Fetch members and settings when expanding
        fetchOrgMembers(orgId);
        adminService.getOrganizationSettings(orgId).then(res => {
          setOrgSettingsMap(prev => ({
            ...prev,
            [orgId]: {
              language: res.data?.language || 'en',
              currency: res.data?.currency || 'USD',
              country: res.data?.country ?? null,
              mission_critical: res.data?.mission_critical ?? false,
              external_enrichment: res.data?.external_enrichment ?? false,
              daily_api_call_limit: res.data?.daily_api_call_limit ?? null,
              api_calls_today: res.data?.api_calls_today ?? null,
              plan_id: res.data?.plan_id ?? null,
              usage_enforcement: res.data?.usage_enforcement ?? 'off',
              usage_month: res.data?.usage_month ?? null,
              billing_plan_id: res.data?.billing_plan_id ?? null,
              billing_plan_name: res.data?.billing_plan_name ?? null,
              quote_monthly_price_cents: res.data?.quote_monthly_price_cents ?? null,
              quote_included_monthly: res.data?.quote_included_monthly ?? null,
              quote_overage_price_cents: res.data?.quote_overage_price_cents ?? null,
              quote_allowance_searches: res.data?.quote_allowance_searches ?? null,
              quote_allowance_images: res.data?.quote_allowance_images ?? null,
              quote_allowance_pdfs: res.data?.quote_allowance_pdfs ?? null,
              roi_calculator_enabled: res.data?.roi_calculator_enabled ?? false,
              roi_b2b_enabled: res.data?.roi_b2b_enabled ?? false,
              roi_b2c_enabled: res.data?.roi_b2c_enabled ?? false,
              roi_monthly_quotes: res.data?.roi_monthly_quotes ?? null,
              roi_fte_multiplier: res.data?.roi_fte_multiplier ?? null,
              roi_currency: res.data?.roi_currency ?? null,
              roi_fte_cost: res.data?.roi_fte_cost ?? null,
              roi_cost_per_quote: res.data?.roi_cost_per_quote ?? null,
              roi_b2c_quotes: res.data?.roi_b2c_quotes ?? null,
              roi_b2c_cr: res.data?.roi_b2c_cr ?? null,
              roi_b2c_aov: res.data?.roi_b2c_aov ?? null,
              roi_b2c_gm: res.data?.roi_b2c_gm ?? null,
              roi_b2c_cpq: res.data?.roi_b2c_cpq ?? null,
              roi_b2c_pct: res.data?.roi_b2c_pct ?? null,
              roi_b2c_pricing_mode: res.data?.roi_b2c_pricing_mode ?? null,
            }
          }));
        }).catch(() => {
          setOrgSettingsMap(prev => ({ ...prev, [orgId]: { language: 'en', currency: 'USD', mission_critical: false, external_enrichment: false, daily_api_call_limit: null, api_calls_today: null, plan_id: null, usage_enforcement: 'off', usage_month: null, billing_plan_id: null, billing_plan_name: null, quote_monthly_price_cents: null, quote_included_monthly: null, quote_overage_price_cents: null, quote_allowance_searches: null, quote_allowance_images: null, quote_allowance_pdfs: null, roi_calculator_enabled: false, roi_b2b_enabled: false, roi_b2c_enabled: false, roi_monthly_quotes: null, roi_fte_multiplier: null, roi_currency: null, roi_fte_cost: null, roi_cost_per_quote: null, roi_b2c_quotes: null, roi_b2c_cr: null, roi_b2c_aov: null, roi_b2c_gm: null, roi_b2c_cpq: null, roi_b2c_pct: null, roi_b2c_pricing_mode: null } }));
        });
      }
      return newSet;
    });
  };

  const handleSaveOrgSettings = async (orgId: number) => {
    const settings = orgSettingsMap[orgId];
    if (!settings) return;
    setOrgSettingsSaving(prev => new Set(prev).add(orgId));
    try {
      await adminService.updateOrganizationSettings(orgId, {
        language: settings.language,
        currency: settings.currency,
        country: settings.country ?? '',
        mission_critical: settings.mission_critical,
        external_enrichment: settings.external_enrichment,
        // 0 tells the backend to clear the cap (empty input = unlimited)
        daily_api_call_limit: settings.daily_api_call_limit ?? 0,
        // "" tells the backend to clear the plan (no plan = unlimited)
        plan_id: settings.plan_id ?? '',
        usage_enforcement: settings.usage_enforcement || 'off',
        // -1 tells the backend to unassign the billing plan
        billing_plan_id: settings.billing_plan_id ?? -1,
        // -1 tells the backend to clear the field back to NULL (empty input)
        quote_monthly_price_cents: settings.quote_monthly_price_cents ?? -1,
        quote_included_monthly: settings.quote_included_monthly ?? -1,
        quote_overage_price_cents: settings.quote_overage_price_cents ?? -1,
        quote_allowance_searches: settings.quote_allowance_searches ?? -1,
        quote_allowance_images: settings.quote_allowance_images ?? -1,
        quote_allowance_pdfs: settings.quote_allowance_pdfs ?? -1,
        roi_calculator_enabled: settings.roi_calculator_enabled,
        roi_b2b_enabled: settings.roi_b2b_enabled,
        roi_b2c_enabled: settings.roi_b2c_enabled,
        roi_monthly_quotes: settings.roi_monthly_quotes ?? undefined,
        roi_fte_multiplier: settings.roi_fte_multiplier ?? undefined,
        roi_currency: settings.roi_currency ?? undefined,
        roi_fte_cost: settings.roi_fte_cost ?? undefined,
        roi_cost_per_quote: settings.roi_cost_per_quote ?? undefined,
        roi_b2c_quotes: settings.roi_b2c_quotes ?? undefined,
        roi_b2c_cr: settings.roi_b2c_cr ?? undefined,
        roi_b2c_aov: settings.roi_b2c_aov ?? undefined,
        roi_b2c_gm: settings.roi_b2c_gm ?? undefined,
        roi_b2c_cpq: settings.roi_b2c_cpq ?? undefined,
        roi_b2c_pct: settings.roi_b2c_pct ?? undefined,
        roi_b2c_pricing_mode: settings.roi_b2c_pricing_mode ?? undefined,
      });
      showNotification('Success', 'Organization settings saved successfully', 'success');
    } catch (err: any) {
      console.error('Failed to save org settings:', err);
      showNotification('Error', err.response?.data?.detail || 'Failed to save organization settings', 'error');
    } finally {
      setOrgSettingsSaving(prev => {
        const next = new Set(prev);
        next.delete(orgId);
        return next;
      });
    }
  };

  const handleTabChange = (tabName: typeof activeTab) => {
    setActiveTab(tabName);
    navigate(`/admin/${tabName}`);
    if (tabName === 'feature-flags') {
      fetchFeatureFlags();
    }
  };

  const handlePromoteUser = async (userUid: string) => {
    try {
      await adminService.promoteUser(userUid);
      fetchUsers();
    } catch (err) {
      console.error('Failed to promote user:', err);
      showNotification('Failed to Promote User', 'Could not promote the user. Please try again.', 'error');
    }
  };

  const handleDemoteUser = async (userUid: string) => {
    try {
      await adminService.demoteUser(userUid);
      fetchUsers();
    } catch (err) {
      console.error('Failed to demote user:', err);
      showNotification('Failed to Demote User', 'Could not demote the user. Please try again.', 'error');
    }
  };

  const handleChangeUserStatus = async (userUid: string, currentStatus: boolean, userName: string) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    const actionTitle = currentStatus ? 'Deactivate User' : 'Activate User';
    const actionMessage = currentStatus
      ? `Are you sure you want to deactivate "${userName}"? They will no longer be able to access the system.`
      : `Are you sure you want to activate "${userName}"? They will be able to access the system again.`;

    showConfirmDialog(
      actionTitle,
      actionMessage,
      async () => {
        try {
          await adminService.updateUserStatus(userUid, !currentStatus);
          fetchUsers();
          showNotification('Success', `User ${userName} has been ${action}d successfully.`, 'success');
        } catch (err) {
          console.error(`Failed to ${action} user:`, err);
          showNotification(`Failed to ${actionTitle}`, `Could not ${action} the user. Please try again.`, 'error');
        }
      },
      currentStatus ? 'danger' : 'info',
      currentStatus ? 'Deactivate' : 'Activate',
      'Cancel'
    );
  };

  const handleSendWelcomeEmail = async () => {
    if (!welcomeEmailUser) return;
    const { uid, email } = welcomeEmailUser;
    setSendingWelcomeEmailUid(uid);
    try {
      await adminService.sendWelcomeEmail(uid, welcomeEmailPassword.trim() || undefined, welcomeEmailLocale);
      setWelcomeEmailUser(null);
      setWelcomeEmailPassword('');
      showNotification('Welcome Email Sent', `The welcome email was sent to ${email}.`, 'success');
    } catch (err: any) {
      console.error('Failed to send welcome email:', err);
      showNotification(
        'Failed to Send Welcome Email',
        err.response?.data?.detail || 'Could not send the welcome email. Please try again.',
        'error'
      );
    } finally {
      setSendingWelcomeEmailUid(null);
    }
  };

  const handleImpersonateUser = async (userUid: string, userName: string) => {
    showConfirmDialog(
      'Impersonate User',
      `Are you sure you want to impersonate "${userName}"? You will see the application as this user until you stop impersonation.`,
      async () => {
        try {
          await startImpersonation(userUid);
          showNotification(
            'Impersonation Started',
            `You are now impersonating ${userName}. Navigate to any page to see the app as this user.`,
            'success'
          );
          // Navigate to the home page to see the impersonated view
          navigate('/');
        } catch (err: any) {
          console.error('Failed to impersonate user:', err);
          showNotification(
            'Failed to Impersonate',
            err.response?.data?.detail || 'Could not impersonate the user. Please try again.',
            'error'
          );
        }
      },
      'warning',
      'Impersonate',
      'Cancel'
    );
  };

  const handleCreateUser = async () => {
    if (!newUserData.name.trim() || !newUserData.email.trim() || newUserData.password.length < 6) {
      showNotification('Validation Error', 'First name, email, and a password of at least 6 characters are required.', 'warning');
      return;
    }
    setAddUserLoading(true);
    try {
      await adminService.createUser({
        name: newUserData.name.trim(),
        middle_name: newUserData.middle_name.trim() || undefined,
        last_name: newUserData.last_name.trim() || undefined,
        email: newUserData.email.trim(),
        password: newUserData.password,
        role: newUserData.role,
        send_welcome_email: newUserData.send_welcome_email,
        welcome_email_locale: newUserData.welcome_email_locale,
        organization_id: newUserData.organization_id,
        organization_role: newUserData.organization_role
      });
      showNotification(
        'User Created',
        `User "${newUserData.name}" has been created successfully.${newUserData.send_welcome_email ? ' A welcome email has been sent.' : ''}${newUserData.organization_id ? ' Added to organization.' : ''}`,
        'success'
      );
      setShowAddUser(false);
      setNewUserData({ name: '', middle_name: '', last_name: '', email: '', password: '', role: 'user', send_welcome_email: true, welcome_email_locale: 'en', organization_id: null, organization_role: 'member' });
      // Refresh users list
      fetchUsers();
    } catch (err: any) {
      console.error('Failed to create user:', err);
      showNotification(
        'Failed to Create User',
        err.response?.data?.detail || 'Could not create the user. Please try again.',
        'error'
      );
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleSaveEditUser = async () => {
    if (!editingUser) return;
    if (!editUserForm.name.trim() || !editUserForm.email.trim()) {
      showNotification('Validation Error', 'Name and email are required.', 'warning');
      return;
    }
    setEditUserSaving(true);
    try {
      await adminService.updateUser(editingUser.uid, {
        name: editUserForm.name.trim(),
        email: editUserForm.email.trim(),
        role: editUserForm.role,
        organization_id: editUserForm.organization_id,
      });
      showNotification('Success', `User "${editUserForm.name}" updated successfully.`, 'success');
      setEditingUser(null);
      fetchUsers();
    } catch (err: any) {
      showNotification('Error', err.response?.data?.detail || 'Failed to update user.', 'error');
    } finally {
      setEditUserSaving(false);
    }
  };

  const toggleSessionExpansion = (sessionId: string) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    try {
      // Handle various timestamp formats
      let date: Date;

      // If it's a numeric string (integer or float Unix timestamp)
      if (/^\d+(\.\d+)?$/.test(dateString)) {
        const timestamp = parseFloat(dateString);
        // Check if it's in seconds (≤13 digits before decimal) or milliseconds
        date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
      } else {
        // Regular date string
        date = new Date(dateString);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString;
      }

      // Format as relative time for recent dates
      const now = new Date();
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

      if (diffInHours < 1) {
        const diffInMinutes = Math.floor(diffInHours * 60);
        return diffInMinutes <= 0 ? 'Just now' : `${diffInMinutes}m ago`;
      } else if (diffInHours < 24) {
        return `${Math.floor(diffInHours)}h ago`;
      } else if (diffInHours < 24 * 7) {
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays}d ago`;
      } else {
        // For older dates, show full date
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    } catch {
      return dateString;
    }
  };

  if (error) {
    return (
      <AppLayout pageTitle="Admin Panel">
        <MobileHeader title="Admin Panel" />
        <div className="flex-1 overflow-auto bg-main-bg min-h-full">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center">
                <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
                <div>
                  <h3 className="text-red-800 font-medium">Access Error</h3>
                  <p className="text-red-600 mt-1">{error}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle="Admin Panel">
      <MobileHeader title="Admin Panel" />
      <div className="flex-1 overflow-auto bg-main-bg min-h-full">
        <div className="max-w-7xl mx-auto px-4 py-8">

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-8">
            <nav className="-mb-px flex space-x-4 overflow-x-auto">
              {[
                { id: 'users', label: 'Users & Orgs', icon: Users },
                { id: 'feedback', label: 'Feedback', icon: Star },
                { id: 'workflows', label: 'Shared Config', icon: Zap },
                { id: 'billing-plans', label: 'Plans', icon: DollarSign },
                { id: 'feature-flags', label: 'Feature Flags', icon: ToggleRight },
                // { id: 'knowledge', label: 'Knowledge', icon: BookOpen },  // hidden; still at /admin/knowledge
                // { id: 'dashboard', label: 'Org Dashboard', icon: Activity },
                { id: 'llm-costs', label: 'LLM Cost', icon: DollarSign },
                { id: 'system', label: 'System', icon: Server },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => handleTabChange(id as typeof activeTab)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${(SUB_TABS[id] || [{ id }]).some(t => t.id === activeTab)
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {label}
                </button>
              ))}
            </nav>
          </div>

          {/* Grouped tabs: each sub-tab keeps its own activeTab value, so URLs,
              data loading and cross-links are unchanged (see SUB_TABS). */}
          {Object.values(SUB_TABS).some(g => g.some(t => t.id === activeTab)) && (
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-6">
                {Object.values(SUB_TABS).find(g => g.some(t => t.id === activeTab))!.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => handleTabChange(id as typeof activeTab)}
                    className={`flex items-center py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {label}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Tab Content Loading */}
          {loading && (
            <div className="flex items-center justify-center py-24">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading admin panel...</p>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {!loading && activeTab === 'users' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">System Users</h2>
                <button
                  onClick={() => {
                    setShowAddUser(true);
                    if (organizations.length === 0) {
                      adminService.getOrganizations().then(res => setOrganizations(res.data.organizations)).catch(() => {});
                    }
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </button>
              </div>

              {/* Search & filter bar */}
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[180px] max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search name or email…"
                    value={userSearchDraft}
                    onChange={e => setUserSearchDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { setUserSearchQuery(userSearchDraft); fetchUsers(0, userSearchDraft, userRoleFilter, userActiveFilter); } }}
                    className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <select
                  value={userRoleFilter}
                  onChange={e => { setUserRoleFilter(e.target.value); fetchUsers(0, userSearchQuery, e.target.value, userActiveFilter); }}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">All roles</option>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </select>
                <select
                  value={userActiveFilter}
                  onChange={e => { setUserActiveFilter(e.target.value); fetchUsers(0, userSearchQuery, userRoleFilter, e.target.value); }}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">All statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <select
                  value={userOrgFilter}
                  onChange={e => { setUserOrgFilter(e.target.value); fetchUsers(0, userSearchQuery, userRoleFilter, userActiveFilter, e.target.value); }}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 max-w-[200px]"
                >
                  <option value="">All organizations</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
                <button
                  onClick={() => { setUserSearchQuery(userSearchDraft); fetchUsers(0, userSearchDraft, userRoleFilter, userActiveFilter); }}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1"
                >
                  <Search className="w-3.5 h-3.5" /> Search
                </button>
                <span className="ml-auto text-xs text-gray-400">{usersTotal} user{usersTotal !== 1 ? 's' : ''}</span>
              </div>

              {usersLoading ? (
                <div className="px-6 py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading users...</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sessions</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {users.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="px-6 py-10 text-center text-gray-400">
                              <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                              No users match your filters.
                            </td>
                          </tr>
                        ) : users.map((user) => (
                          <tr key={user.uid} className={!user.is_active ? 'bg-gray-50 opacity-75' : ''}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {user.picture ? (
                                  <img className="h-10 w-10 rounded-full" src={user.picture} alt="" />
                                ) : (
                                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                    <Users className="h-6 w-6 text-gray-400" />
                                  </div>
                                )}
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                  <div className="text-xs text-gray-400">ID: {user.uid}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                                {user.role === 'admin' && <Crown className="w-3 h-3 mr-1" />}
                                {user.role}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {user.organization_name
                                ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{user.organization_name}</span>
                                : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {user.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {user.last_login ? formatDate(String(user.last_login)) : <span className="text-gray-400">Never</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-medium">{user.session_count ?? 0}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center gap-2 flex-wrap">
                                <button
                                  onClick={() => {
                                    setEditingUser(user);
                                    setEditUserForm({ name: user.name, email: user.email, role: user.role, organization_id: user.organization_id ?? null });
                                    if (organizations.length === 0) {
                                      adminService.getOrganizations().then(res => setOrganizations(res.data.organizations)).catch(() => {});
                                    }
                                  }}
                                  className="text-gray-600 hover:text-gray-900 flex items-center"
                                  title="Edit user"
                                >
                                  <Edit className="w-4 h-4 mr-1" />Edit
                                </button>
                                <button
                                  onClick={() => { setWelcomeEmailUser(user); setWelcomeEmailPassword(''); setWelcomeEmailLocale('en'); }}
                                  disabled={sendingWelcomeEmailUid === user.uid}
                                  className="text-teal-600 hover:text-teal-900 disabled:opacity-50 flex items-center"
                                  title="Send welcome email"
                                >
                                  <Mail className="w-4 h-4 mr-1" />
                                  {sendingWelcomeEmailUid === user.uid ? 'Sending…' : 'Welcome Email'}
                                </button>
                                {user.uid !== currentUser?.uid && (
                                  <button
                                    onClick={() => handleChangeUserStatus(user.uid, user.is_active, user.name)}
                                    className={`${user.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'} flex items-center`}
                                  >
                                    {user.is_active ? <><UserX className="w-4 h-4 mr-1" />Deactivate</> : <><UserCheck className="w-4 h-4 mr-1" />Activate</>}
                                  </button>
                                )}
                                {user.uid !== currentUser?.uid && user.is_active && (
                                  <button onClick={() => handleImpersonateUser(user.uid, user.name)} className="text-blue-600 hover:text-blue-900 flex items-center">
                                    <UserCheck className="w-4 h-4 mr-1" />Impersonate
                                  </button>
                                )}
                                {user.role === 'admin' && user.uid !== currentUser?.uid ? (
                                  <button onClick={() => handleDemoteUser(user.uid)} className="text-orange-600 hover:text-orange-900 flex items-center">
                                    <UserX className="w-4 h-4 mr-1" />Demote
                                  </button>
                                ) : user.role === 'user' ? (
                                  <button onClick={() => handlePromoteUser(user.uid)} className="text-purple-600 hover:text-purple-900 flex items-center">
                                    <Crown className="w-4 h-4 mr-1" />Promote
                                  </button>
                                ) : user.uid === currentUser?.uid ? (
                                  <span className="text-gray-400">Current User</span>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Server-side Pagination */}
                  {(() => {
                    const totalPages = Math.ceil(usersTotal / USERS_PAGE_SIZE);
                    if (totalPages <= 1) return null;
                    return (
                      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          Page {usersPage + 1} of {totalPages} · {usersTotal} users
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => { setUsersPage(0); fetchUsers(0); }}
                            disabled={usersPage === 0}
                            className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                          >«</button>
                          <button
                            onClick={() => { const p = usersPage - 1; setUsersPage(p); fetchUsers(p); }}
                            disabled={usersPage === 0}
                            className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                          >Prev</button>
                          {Array.from({ length: totalPages }, (_, i) => i)
                            .filter(i => Math.abs(i - usersPage) <= 2)
                            .map(i => (
                              <button
                                key={i}
                                onClick={() => { setUsersPage(i); fetchUsers(i); }}
                                className={`px-3 py-1 text-sm border rounded ${i === usersPage ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 hover:bg-gray-50'}`}
                              >{i + 1}</button>
                            ))}
                          <button
                            onClick={() => { const p = usersPage + 1; setUsersPage(p); fetchUsers(p); }}
                            disabled={usersPage >= totalPages - 1}
                            className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                          >Next</button>
                          <button
                            onClick={() => { const p = totalPages - 1; setUsersPage(p); fetchUsers(p); }}
                            disabled={usersPage >= totalPages - 1}
                            className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                          >»</button>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {/* Edit User Modal */}
          {editingUser && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Edit className="w-4 h-4 text-blue-500" />
                    Edit User
                  </h3>
                  <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="px-6 py-5 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      value={editUserForm.name}
                      onChange={e => setEditUserForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={editUserForm.email}
                      onChange={e => setEditUserForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                      value={editUserForm.role}
                      onChange={e => setEditUserForm(f => ({ ...f, role: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                    <select
                      value={editUserForm.organization_id ?? ''}
                      onChange={e => setEditUserForm(f => ({ ...f, organization_id: e.target.value ? Number(e.target.value) : null }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                    >
                      <option value="">— No organization —</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                  <button
                    onClick={() => setEditingUser(null)}
                    className="px-4 py-2 text-sm border border-gray-300 text-gray-600 rounded-md hover:bg-gray-100"
                  >Cancel</button>
                  <button
                    onClick={handleSaveEditUser}
                    disabled={editUserSaving}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {editUserSaving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    <Save className="w-4 h-4" />
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}

          {welcomeEmailUser && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-teal-500" />
                    Send Welcome Email
                  </h3>
                  <button onClick={() => setWelcomeEmailUser(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="px-6 py-5 space-y-4">
                  <p className="text-sm text-gray-600">
                    Send the welcome email to <strong>{welcomeEmailUser.name}</strong> at{' '}
                    <span className="font-mono text-gray-900">{welcomeEmailUser.email}</span>.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                    <select
                      value={welcomeEmailLocale}
                      onChange={e => setWelcomeEmailLocale(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
                    >
                      {WELCOME_EMAIL_LANGUAGES.map(lang => (
                        <option key={lang.code} value={lang.code}>{lang.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={welcomeEmailPassword}
                      onChange={e => setWelcomeEmailPassword(e.target.value)}
                      placeholder="Leave empty to omit sign-in details"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-400"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      When set, the email tells the user their username is <span className="font-mono">{welcomeEmailUser.email}</span> and quotes this password.
                      It is only relayed — it is not stored and does not change the account's actual password.
                    </p>
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                  <button
                    onClick={() => setWelcomeEmailUser(null)}
                    className="px-4 py-2 text-sm border border-gray-300 text-gray-600 rounded-md hover:bg-gray-100"
                  >Cancel</button>
                  <button
                    onClick={handleSendWelcomeEmail}
                    disabled={sendingWelcomeEmailUid === welcomeEmailUser.uid}
                    className="px-4 py-2 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {sendingWelcomeEmailUid === welcomeEmailUser.uid && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    <Mail className="w-4 h-4" />
                    Send Email
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Workflows Tab */}
          {!loading && activeTab === 'workflows' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-500" />
                    Workflows — Pipeline Configuration
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Configure automation pipelines and state machines. Workflows can be defined at the <strong>shared (default)</strong> level — applying to all organisations — or overridden per organisation.
                  </p>
                </div>

                {/* Scope Selector */}
                <div className="px-6 py-4 flex flex-wrap items-end gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Workflow scope</label>
                    <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
                      <button
                        onClick={async () => {
                          setWorkflowsScope('shared');
                          setWorkflowsOrgId(null);
                          setWorkflowsStateConfig(null);
                          if (!workflowsSharedConfig) {
                            setWorkflowsSharedLoading(true);
                            try {
                              const r = await workflowService.getStateConfig(undefined, true);
                              setWorkflowsSharedConfig(r.data);
                            } catch { /* ignore */ }
                            finally { setWorkflowsSharedLoading(false); }
                          }
                        }}
                        className={`px-4 py-2 font-medium transition-colors ${workflowsScope === 'shared' ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      >
                        🌐 Shared / Default
                      </button>
                      <button
                        onClick={() => {
                          setWorkflowsScope('org');
                        }}
                        className={`px-4 py-2 font-medium border-l border-gray-200 transition-colors ${workflowsScope === 'org' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      >
                        🏢 Organisation-specific
                      </button>
                    </div>
                  </div>

                  {workflowsScope === 'org' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Organisation</label>
                      <div className="flex items-center gap-3 flex-wrap">
                        <select
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          value={workflowsOrgId ?? ''}
                          onChange={async (e) => {
                            const id = e.target.value ? Number(e.target.value) : null;
                            setWorkflowsOrgId(id);
                            setWorkflowsStateConfig(null);
                            if (id !== null) {
                              setWorkflowsStateLoading(true);
                              try {
                                const r = await workflowService.getStateConfig(id);
                                setWorkflowsStateConfig(r.data);
                              } catch { /* ignore */ }
                              finally { setWorkflowsStateLoading(false); }
                            }
                          }}
                        >
                          <option value="">— select an organisation —</option>
                          {organizations.map(org => (
                            <option key={org.id} value={org.id}>{org.name}</option>
                          ))}
                        </select>
                        {organizations.length === 0 && (
                          <button
                            onClick={() => { handleTabChange('organizations'); }}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            Create an organisation first →
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {workflowsScope === 'shared' && (
                  <div className="px-6 pb-4">
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                      <Zap className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        The <strong>Shared / Default</strong> config applies to all organisations that have not defined their own override. Changes here affect every org unless they have a custom config.
                      </span>
                    </div>
                  </div>
                )}

                {workflowsScope === 'org' && workflowsOrgId && (
                  <div className="px-6 pb-4">
                    <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                      <Database className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        Showing the workflow config for <strong>{organizations.find(o => o.id === workflowsOrgId)?.name ?? `Org #${workflowsOrgId}`}</strong>.
                        If no custom config exists, the shared default is used and shown here. Saving creates an org-specific override.
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Shared scope content */}
              {workflowsScope === 'shared' && (
                <div className="bg-white rounded-lg shadow px-6 py-6">
                  {workflowsSharedLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
                    </div>
                  ) : workflowsSharedConfig ? (
                    <div className="space-y-8">
                      <AutomationsTab states={workflowsSharedConfig.states} isShared={true} />
                      <StateMachineTab
                        config={workflowsSharedConfig}
                        onConfigChange={(updated) => setWorkflowsSharedConfig(updated)}
                      />
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Zap className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm text-gray-500 mb-4">Click below to load the shared default workflow configuration.</p>
                      <button
                        onClick={async () => {
                          setWorkflowsSharedLoading(true);
                          try {
                            const r = await workflowService.getStateConfig(undefined, true);
                            setWorkflowsSharedConfig(r.data);
                          } catch { /* ignore */ }
                          finally { setWorkflowsSharedLoading(false); }
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-md hover:bg-amber-600"
                      >
                        <Zap className="w-4 h-4" /> Load shared config
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Org-specific scope content */}
              {workflowsScope === 'org' && workflowsOrgId !== null && (
                <div className="bg-white rounded-lg shadow px-6 py-6">
                  {workflowsStateLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                  ) : workflowsStateConfig ? (
                    <div className="space-y-8">
                      <AutomationsTab states={workflowsStateConfig.states} orgId={workflowsOrgId} />
                      <StateMachineTab
                        config={workflowsStateConfig}
                        onConfigChange={(updated) => setWorkflowsStateConfig(updated)}
                        orgId={workflowsOrgId}
                      />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-8">Could not load state config for this organisation.</p>
                  )}
                </div>
              )}

              {workflowsScope === 'org' && workflowsOrgId === null && (
                <div className="bg-white rounded-lg shadow px-6 py-12 text-center text-gray-400">
                  <Database className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                  <p className="text-sm">Select an organisation above to view or edit its workflow configuration.</p>
                </div>
              )}
            </div>
          )}

          {/* Shared Integrations Tab */}
          {!loading && activeTab === 'shared-integrations' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Database className="w-5 h-5 text-indigo-500" />
                    Shared Integration Sources
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Define data-source configs once and share them across multiple organisations.
                    Any source you create here will be available as a shared source for the selected organisations.
                  </p>
                </div>
              </div>

              {/* Integration setup page embedded in admin mode — everything is a
                  data source (supplier); the old inventory type was folded in. */}
              <GenericIntegrationSetupPage
                adminMode={true}
                integrationId="supplier"
                organizations={organizations}
              />
            </div>
          )}

          {/* Shared Catalogs Tab */}
          {!loading && activeTab === 'shared-catalogs' && (
            <SharedCatalogsTab organizations={organizations} />
          )}


          {!loading && activeTab === 'billing-plans' && (
            <BillingPlansTab />
          )}

          {/* Sessions Tab */}
          {!loading && activeTab === 'sessions' && (
            <div className="space-y-4">
              {/* Sessions Header with Stats */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-medium text-gray-900">User Sessions</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        {sessionsTotal > 0 ? (
                          <>Showing {sessions.length} of {sessionsTotal} sessions</>
                        ) : (
                          'All chat sessions across all users'
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Sort Order */}
                      <button
                        onClick={() => {
                          const newOrder = sessionSortOrder === 'newest' ? 'oldest' : 'newest';
                          setSessionSortOrder(newOrder);
                        }}
                        className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                        title={sessionSortOrder === 'newest' ? 'Showing newest first' : 'Showing oldest first'}
                      >
                        {sessionSortOrder === 'newest' ? <ArrowDown className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
                        {sessionSortOrder === 'newest' ? 'Newest' : 'Oldest'}
                      </button>
                      {/* Refresh */}
                      <button
                        onClick={() => fetchSessions(sessionUserFilter)}
                        className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50"
                        disabled={sessionsLoading}
                      >
                        <RefreshCw className={`w-4 h-4 ${sessionsLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                    </div>
                  </div>
                </div>

                {/* Filter Bar */}
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1 max-w-md">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Filter by User ID..."
                        value={sessionUserFilter}
                        onChange={(e) => setSessionUserFilter(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            fetchSessions(sessionUserFilter, 0);
                          }
                        }}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <button
                      onClick={() => fetchSessions(sessionUserFilter, 0)}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center gap-1"
                    >
                      <Filter className="w-4 h-4" />
                      Apply
                    </button>
                    {sessionUserFilter && (
                      <button
                        onClick={() => {
                          setSessionUserFilter('');
                          fetchSessions('', 0);
                        }}
                        className="px-4 py-2 text-gray-600 text-sm border border-gray-300 rounded-md hover:bg-gray-100 flex items-center gap-1"
                      >
                        <X className="w-4 h-4" />
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Sessions Table */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                {sessionsLoading ? (
                  <div className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-3 text-gray-600">Loading sessions...</p>
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500 font-medium">No sessions found</p>
                    {sessionUserFilter && (
                      <p className="text-sm text-gray-400 mt-1">Try a different user ID filter</p>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">App</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {(sessionSortOrder === 'oldest' ? [...sessions].reverse() : sessions).map((session) => (
                            <React.Fragment key={session.id}>
                              <tr
                                className={`hover:bg-gray-50 cursor-pointer transition-colors ${expandedSessions.has(session.id) ? 'bg-blue-50' : ''}`}
                                onClick={() => toggleSessionExpansion(session.id)}
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    {session.user_picture ? (
                                      <img className="h-8 w-8 rounded-full flex-shrink-0" src={session.user_picture} alt="" />
                                    ) : (
                                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                        <Users className="h-4 w-4 text-gray-400" />
                                      </div>
                                    )}
                                    <div className="ml-3 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {session.user_name || 'Unknown User'}
                                      </p>
                                      <p className="text-xs text-gray-500 truncate">{session.user_email}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div>
                                    <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono text-gray-700">
                                      {session.id.length > 12 ? `...${session.id.slice(-12)}` : session.id}
                                    </code>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                    {session.app_name}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center text-sm text-gray-600">
                                    <Clock className="w-3.5 h-3.5 mr-1 text-gray-400" />
                                    {session.last_update_time ? formatDate(String(session.last_update_time)) : 'N/A'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {session.state_keys && session.state_keys.length > 0 ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" title={session.state_keys.join(', ')}>
                                      {session.state_keys.length} keys
                                    </span>
                                  ) : session.state && Object.keys(session.state).length > 0 ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      {Object.keys(session.state).length} keys
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Empty</span>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                    <button
                                      onClick={() => navigate(`/admin/sessions/${session.user_id}/${session.id}`)}
                                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                      title="View chat messages"
                                    >
                                      <Eye className="w-4 h-4" />
                                      <span className="hidden sm:inline">View</span>
                                    </button>
                                    <button
                                      onClick={() => toggleSessionExpansion(session.id)}
                                      className="text-gray-400 hover:text-gray-600"
                                    >
                                      {expandedSessions.has(session.id) ? (
                                        <ChevronDown className="w-4 h-4" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4" />
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>

                              {/* Expanded Details Row */}
                              {expandedSessions.has(session.id) && (
                                <tr>
                                  <td colSpan={6} className="px-6 py-4 bg-gray-50">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {/* Session Info */}
                                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Session Details</h4>
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Full ID:</span>
                                            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono max-w-[200px] truncate" title={session.id}>
                                              {session.id}
                                            </code>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">App:</span>
                                            <span className="font-medium">{session.app_name}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Last Active:</span>
                                            <span className="font-medium">
                                              {session.last_update_time
                                                ? new Date(
                                                  session.last_update_time < 10000000000
                                                    ? session.last_update_time * 1000
                                                    : session.last_update_time
                                                ).toLocaleString()
                                                : 'N/A'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">State Keys:</span>
                                            <span className="font-medium">{session.state_keys?.length || Object.keys(session.state || {}).length}</span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* User Info */}
                                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">User Info</h4>
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Name:</span>
                                            <span className="font-medium">{session.user_name || 'Unknown'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Email:</span>
                                            <span className="font-medium truncate max-w-[180px]">{session.user_email || 'Unknown'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">User ID:</span>
                                            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono max-w-[180px] truncate" title={session.user_id}>
                                              {session.user_id}
                                            </code>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Session State — full-width JSON tree */}
                                    <div className="mt-4 bg-white rounded-lg border border-gray-200">
                                      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
                                        <h4 className="text-xs font-semibold text-gray-500 uppercase">Session State</h4>
                                        {session.state && Object.keys(session.state).length > 0 && (
                                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                                            {Object.keys(session.state).length} keys
                                          </span>
                                        )}
                                      </div>
                                      {session.state && Object.keys(session.state).length > 0 ? (
                                        <JsonTreeViewer data={session.state} maxHeightClass="max-h-72" />
                                      ) : (
                                        <p className="px-4 py-3 text-sm text-gray-400 italic">No state data</p>
                                      )}
                                    </div>

                                    {/* Quick Actions */}
                                    <div className="mt-3 flex items-center gap-3">
                                      <button
                                        onClick={() => navigate(`/admin/sessions/${session.user_id}/${session.id}`)}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                      >
                                        <Eye className="w-4 h-4" />
                                        View Full Chat
                                      </button>
                                      <button
                                        onClick={() => {
                                          setSessionUserFilter(session.user_id);
                                          fetchSessions(session.user_id, 0);
                                        }}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100"
                                      >
                                        <Filter className="w-4 h-4" />
                                        Show all sessions for this user
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {sessionsTotal > 0 && (
                      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                        <p className="text-sm text-gray-600">
                          Showing {sessionsOffset + 1}–{Math.min(sessionsOffset + sessions.length, sessionsTotal)} of {sessionsTotal} sessions
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => fetchSessions(sessionUserFilter, Math.max(0, sessionsOffset - 50))}
                            disabled={sessionsOffset === 0 || sessionsLoading}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => fetchSessions(sessionUserFilter, sessionsOffset + 50)}
                            disabled={!sessionsHasMore || sessionsLoading}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Agents Tab */}
          {!loading && activeTab === 'agents' && (
            <div className="bg-white rounded-lg shadow">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Agents
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  View and edit agent instruction files and skill configurations
                </p>
              </div>

              {/* Sub-tabs */}
              <div className="border-b border-gray-200 px-6">
                <nav className="-mb-px flex space-x-6">
                  {[
                    { id: 'instructions', label: 'Instructions', icon: FileText },
                    { id: 'skills', label: 'Skills', icon: BookOpen },
                  ].map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => {
                        setAgentsSubTab(id as 'instructions' | 'skills');
                        setSelectedAgent(null);
                        setSelectedSkill(null);
                        setInstructionEditMode('view');
                        setSkillEditMode('view');
                        // Lazy-load if data hasn't arrived yet
                        if (id === 'skills' && agentSkills.length === 0 && !skillsLoading) {
                          fetchAgentSkills();
                        }
                        if (id === 'instructions' && agentInstructions.length === 0 && !agentsLoading) {
                          fetchAgentInstructions();
                        }
                      }}
                      className={`flex items-center py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${agentsSubTab === id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* ── INSTRUCTIONS SUB-TAB ── */}
              {agentsSubTab === 'instructions' && (
                agentsLoading ? (
                  <div className="px-6 py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading agent instructions...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-3 divide-x divide-gray-200">
                    {/* Agent List */}
                    <div className="lg:col-span-1 p-4 bg-gray-50 max-h-[700px] overflow-y-auto">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3 sticky top-0 bg-gray-50 py-2">
                        Available Agents ({(agentInstructions ?? []).length})
                      </h3>
                      <div className="space-y-2">
                        {(agentInstructions ?? []).map((agent) => (
                          <button
                            key={agent.folder}
                            onClick={() => {
                              setSelectedAgent(agent);
                              setEditingInstructionContent(agent.content || '');
                              setInstructionEditMode('view');
                            }}
                            className={`w-full text-left p-3 rounded-lg border transition-all ${selectedAgent?.folder === agent.folder
                              ? 'bg-blue-50 border-blue-300 shadow-sm'
                              : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                              }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Code className={`w-4 h-4 ${agent.error ? 'text-red-500' : 'text-blue-500'}`} />
                                  <span className="font-medium text-sm text-gray-900">{agent.agent_name}</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{agent.file_name}</p>
                                {agent.error ? (
                                  <p className="text-xs text-red-600 mt-1 flex items-center">
                                    <AlertCircle className="w-3 h-3 mr-1" />{agent.error}
                                  </p>
                                ) : (
                                  <div className="text-xs text-gray-400 mt-1">
                                    {agent.lines} lines • {(agent.size_bytes! / 1024).toFixed(1)} KB
                                  </div>
                                )}
                              </div>
                              {selectedAgent?.folder === agent.folder && (
                                <ChevronRight className="w-5 h-5 text-blue-500 flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Instruction Content / Editor */}
                    <div className="lg:col-span-2 p-6 max-h-[700px] overflow-y-auto">
                      {selectedAgent ? (
                        <div>
                          {/* Toolbar */}
                          <div className="mb-4 pb-4 border-b border-gray-200 flex items-start justify-between gap-4">
                            <div>
                              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <Code className="w-6 h-6 text-blue-500" />
                                {selectedAgent.agent_name}
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                <span className="font-medium">File:</span> {selectedAgent.file_name}
                              </p>
                              {selectedAgent.lines && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {selectedAgent.lines} lines • {(selectedAgent.size_bytes! / 1024).toFixed(2)} KB
                                </p>
                              )}
                            </div>
                            {!selectedAgent.error && (
                              <div className="flex gap-2 flex-shrink-0">
                                {instructionEditMode === 'view' ? (
                                  <button
                                    onClick={() => {
                                      setEditingInstructionContent(selectedAgent.content || '');
                                      setInstructionEditMode('edit');
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                  >
                                    <Edit className="w-4 h-4" /> Edit
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => setInstructionEditMode('view')}
                                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                    >
                                      <X className="w-4 h-4" /> Cancel
                                    </button>
                                    <button
                                      disabled={instructionSaving}
                                      onClick={async () => {
                                        setInstructionSaving(true);
                                        try {
                                          await adminService.updateAgentInstruction(selectedAgent.folder, editingInstructionContent);
                                          // Refresh agent list
                                          const updatedInstructions = agentInstructions.map(a =>
                                            a.folder === selectedAgent.folder
                                              ? { ...a, content: editingInstructionContent, lines: editingInstructionContent.split('\n').length, size_bytes: new TextEncoder().encode(editingInstructionContent).length }
                                              : a
                                          );
                                          setAgentInstructions(updatedInstructions);
                                          setSelectedAgent({ ...selectedAgent, content: editingInstructionContent });
                                          setInstructionEditMode('view');
                                          showNotification('Saved', `Instruction for "${selectedAgent.agent_name}" saved successfully.`, 'success');
                                        } catch (err) {
                                          showNotification('Save Failed', 'Could not save the instruction file. Please try again.', 'error');
                                        } finally {
                                          setInstructionSaving(false);
                                        }
                                      }}
                                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60"
                                    >
                                      {instructionSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                      {instructionSaving ? 'Saving…' : 'Save'}
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          {selectedAgent.error ? (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                              <div className="flex items-center gap-2 text-red-700">
                                <AlertCircle className="w-5 h-5" />
                                <span className="font-medium">Error Loading File</span>
                              </div>
                              <p className="text-red-600 mt-2">{selectedAgent.error}</p>
                            </div>
                          ) : instructionEditMode === 'edit' ? (
                            <textarea
                              value={editingInstructionContent}
                              onChange={e => setEditingInstructionContent(e.target.value)}
                              className="w-full h-[500px] font-mono text-sm bg-gray-900 text-gray-100 p-4 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                              spellCheck={false}
                            />
                          ) : selectedAgent.content ? (
                            <div className="prose prose-sm max-w-none markdown-content">
                              <ReactMarkdown
                                components={{
                                  h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mt-6 mb-4 text-gray-900" {...props} />,
                                  h2: ({ node, ...props }) => <h2 className="text-xl font-bold mt-5 mb-3 text-gray-900" {...props} />,
                                  h3: ({ node, ...props }) => <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-900" {...props} />,
                                  p: ({ node, ...props }) => <p className="mb-3 text-gray-700 leading-relaxed" {...props} />,
                                  ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-3 space-y-1 text-gray-700" {...props} />,
                                  ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-3 space-y-1 text-gray-700" {...props} />,
                                  li: ({ node, ...props }) => <li className="ml-4" {...props} />,
                                  code: ({ node, inline, ...props }: any) =>
                                    inline ? (
                                      <code className="bg-gray-100 text-red-600 px-1.5 py-0.5 rounded text-sm font-mono" {...props} />
                                    ) : (
                                      <code className="block bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono my-3" {...props} />
                                    ),
                                  pre: ({ node, ...props }) => <pre className="bg-gray-900 rounded-lg overflow-hidden my-3" {...props} />,
                                  blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-600 my-3" {...props} />,
                                  a: ({ node, ...props }) => <a className="text-blue-600 hover:text-blue-800 underline" {...props} />,
                                  strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
                                }}
                              >
                                {selectedAgent.content}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            <div className="text-center text-gray-500 py-8">
                              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                              <p>No content available</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full text-center text-gray-500 py-12">
                          <div>
                            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <p className="text-lg font-medium">Select an agent to view instructions</p>
                            <p className="text-sm mt-2">Choose an agent from the list on the left</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              )}

              {/* ── SKILLS SUB-TAB ── */}
              {agentsSubTab === 'skills' && (
                skillsLoading ? (
                  <div className="px-6 py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading agent skills...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-3 divide-x divide-gray-200">
                    {/* Skills Tree - Left */}
                    <div className="lg:col-span-1 p-4 bg-gray-50 max-h-[700px] overflow-y-auto">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3 sticky top-0 bg-gray-50 py-2">
                        Skills by Agent
                      </h3>
                      {(agentSkills ?? []).length === 0 ? (
                        <p className="text-sm text-gray-500">No skill files found.</p>
                      ) : (
                        <div className="space-y-2">
                          {(agentSkills ?? []).map((agentGroup) => {
                            const isExpanded = expandedSkillAgents.has(agentGroup.agent_folder);
                            return (
                              <div key={agentGroup.agent_folder} className="border border-gray-200 rounded-lg overflow-hidden">
                                {/* Agent header - clickable to collapse/expand */}
                                <button
                                  onClick={() => {
                                    setExpandedSkillAgents(prev => {
                                      const next = new Set(prev);
                                      if (next.has(agentGroup.agent_folder)) {
                                        next.delete(agentGroup.agent_folder);
                                      } else {
                                        next.add(agentGroup.agent_folder);
                                      }
                                      return next;
                                    });
                                  }}
                                  className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2">
                                    <Code className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">{agentGroup.agent_name}</span>
                                    <span className="text-xs text-gray-400 font-normal">({agentGroup.skills.length})</span>
                                  </div>
                                  <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                                </button>

                                {/* Skills list - shown when expanded */}
                                {isExpanded && (
                                  <div className="divide-y divide-gray-100">
                                    {agentGroup.skills.map((skill) => {
                                      const isSelected =
                                        selectedSkill?.agent_folder === agentGroup.agent_folder &&
                                        selectedSkill?.skill_folder === skill.skill_folder &&
                                        selectedSkill?.file_name === skill.file_name;
                                      return (
                                        <button
                                          key={`${skill.skill_folder}/${skill.file_name}`}
                                          onClick={() => {
                                            setSelectedSkill({ ...skill, agent_folder: agentGroup.agent_folder });
                                            setEditingSkillContent(skill.content || '');
                                            setSkillEditMode('view');
                                          }}
                                          className={`w-full text-left px-3 py-2.5 transition-all ${isSelected
                                            ? 'bg-purple-50 border-l-4 border-l-purple-400'
                                            : 'bg-white hover:bg-gray-50 border-l-4 border-l-transparent'
                                            }`}
                                        >
                                          <div className="flex items-center justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-1.5">
                                                <BookOpen className={`w-3.5 h-3.5 flex-shrink-0 ${skill.error ? 'text-red-500' : 'text-purple-400'}`} />
                                                <span className={`font-medium text-xs truncate ${isSelected ? 'text-purple-700' : 'text-gray-800'}`}>{skill.file_name}</span>
                                              </div>
                                              <p className="text-xs text-gray-400 mt-0.5 ml-5">{skill.skill_folder}</p>
                                              {skill.error ? (
                                                <p className="text-xs text-red-500 mt-0.5 ml-5">{skill.error}</p>
                                              ) : (
                                                <p className="text-xs text-gray-400 ml-5">{skill.lines} lines • {((skill.size_bytes || 0) / 1024).toFixed(1)} KB</p>
                                              )}
                                            </div>
                                            {isSelected && <ChevronRight className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />}
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Skill Content / Editor - Right */}
                    <div className="lg:col-span-2 p-6 max-h-[700px] overflow-y-auto">
                      {selectedSkill ? (
                        <div>
                          {/* Toolbar */}
                          <div className="mb-4 pb-4 border-b border-gray-200 flex items-start justify-between gap-4">
                            <div>
                              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <BookOpen className="w-6 h-6 text-purple-500" />
                                {selectedSkill.file_name}
                              </h3>
                              <p className="text-sm text-gray-500 mt-1">
                                <span className="font-medium text-gray-700">{selectedSkill.agent_folder}</span>
                                {' / skills / '}
                                <span className="font-medium text-gray-700">{selectedSkill.skill_folder}</span>
                              </p>
                              {selectedSkill.lines && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {selectedSkill.lines} lines • {((selectedSkill.size_bytes || 0) / 1024).toFixed(2)} KB
                                </p>
                              )}
                            </div>
                            {!selectedSkill.error && (
                              <div className="flex gap-2 flex-shrink-0">
                                {skillEditMode === 'view' ? (
                                  <button
                                    onClick={() => {
                                      setEditingSkillContent(selectedSkill.content || '');
                                      setSkillEditMode('edit');
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                                  >
                                    <Edit className="w-4 h-4" /> Edit
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => setSkillEditMode('view')}
                                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                    >
                                      <X className="w-4 h-4" /> Cancel
                                    </button>
                                    <button
                                      disabled={skillSaving}
                                      onClick={async () => {
                                        setSkillSaving(true);
                                        try {
                                          await adminService.updateAgentSkill(
                                            selectedSkill.agent_folder,
                                            selectedSkill.skill_folder,
                                            selectedSkill.file_name,
                                            editingSkillContent
                                          );
                                          // Update local state
                                          const updatedContent = editingSkillContent;
                                          const updatedSkills = agentSkills.map(ag =>
                                            ag.agent_folder !== selectedSkill.agent_folder ? ag : {
                                              ...ag,
                                              skills: ag.skills.map(s =>
                                                s.skill_folder === selectedSkill.skill_folder && s.file_name === selectedSkill.file_name
                                                  ? { ...s, content: updatedContent, lines: updatedContent.split('\n').length, size_bytes: new TextEncoder().encode(updatedContent).length }
                                                  : s
                                              )
                                            }
                                          );
                                          setAgentSkills(updatedSkills);
                                          setSelectedSkill({ ...selectedSkill, content: updatedContent });
                                          setSkillEditMode('view');
                                          showNotification('Saved', `Skill "${selectedSkill.file_name}" saved successfully.`, 'success');
                                        } catch (err) {
                                          showNotification('Save Failed', 'Could not save the skill file. Please try again.', 'error');
                                        } finally {
                                          setSkillSaving(false);
                                        }
                                      }}
                                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60"
                                    >
                                      {skillSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                      {skillSaving ? 'Saving…' : 'Save'}
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          {selectedSkill.error ? (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                              <div className="flex items-center gap-2 text-red-700">
                                <AlertCircle className="w-5 h-5" />
                                <span className="font-medium">Error Loading File</span>
                              </div>
                              <p className="text-red-600 mt-2">{selectedSkill.error}</p>
                            </div>
                          ) : skillEditMode === 'edit' ? (
                            <textarea
                              value={editingSkillContent}
                              onChange={e => setEditingSkillContent(e.target.value)}
                              className="w-full h-[500px] font-mono text-sm bg-gray-900 text-gray-100 p-4 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                              spellCheck={false}
                            />
                          ) : selectedSkill.content ? (
                            <div className="prose prose-sm max-w-none markdown-content">
                              <ReactMarkdown
                                components={{
                                  h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mt-6 mb-4 text-gray-900" {...props} />,
                                  h2: ({ node, ...props }) => <h2 className="text-xl font-bold mt-5 mb-3 text-gray-900" {...props} />,
                                  h3: ({ node, ...props }) => <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-900" {...props} />,
                                  p: ({ node, ...props }) => <p className="mb-3 text-gray-700 leading-relaxed" {...props} />,
                                  ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-3 space-y-1 text-gray-700" {...props} />,
                                  ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-3 space-y-1 text-gray-700" {...props} />,
                                  li: ({ node, ...props }) => <li className="ml-4" {...props} />,
                                  code: ({ node, inline, ...props }: any) =>
                                    inline ? (
                                      <code className="bg-gray-100 text-red-600 px-1.5 py-0.5 rounded text-sm font-mono" {...props} />
                                    ) : (
                                      <code className="block bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono my-3" {...props} />
                                    ),
                                  pre: ({ node, ...props }) => <pre className="bg-gray-900 rounded-lg overflow-hidden my-3" {...props} />,
                                  blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-600 my-3" {...props} />,
                                  a: ({ node, ...props }) => <a className="text-blue-600 hover:text-blue-800 underline" {...props} />,
                                  strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
                                }}
                              >
                                {selectedSkill.content}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            <div className="text-center text-gray-500 py-8">
                              <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                              <p>No content available</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full text-center text-gray-500 py-12">
                          <div>
                            <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <p className="text-lg font-medium">Select a skill to view or edit</p>
                            <p className="text-sm mt-2">Choose a skill file from the list on the left</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          )}

          {/* Organizations Tab */}
          {!loading && activeTab === 'organizations' && (
            <div className="space-y-6">
              {/* Create Organization Form */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-lg font-medium text-gray-900">Organizations</h2>
                  <button
                    onClick={() => setShowCreateOrg(!showCreateOrg)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Organization
                  </button>
                </div>

                {showCreateOrg && (
                  <div className="p-6 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-md font-medium text-gray-900 mb-4">Create New Organization</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={newOrgData.name}
                          onChange={(e) => setNewOrgData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Organization name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={newOrgData.description}
                          onChange={(e) => setNewOrgData(prev => ({ ...prev, description: e.target.value }))}
                          rows={3}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Organization description"
                        />
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={handleCreateOrganization}
                          disabled={!newOrgData.name}
                          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          Create
                        </button>
                        <button
                          onClick={() => {
                            setShowCreateOrg(false);
                            setNewOrgData({ name: '', description: '' });
                          }}
                          className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 flex items-center"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Organizations List */}
                {organizationsLoading ? (
                  <div className="px-6 py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading organizations...</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {organizations.map((org) => (
                      <div key={org.id} className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            {editingOrg?.id === org.id ? (
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                                  <input
                                    type="text"
                                    value={editingOrg.name}
                                    onChange={(e) => setEditingOrg({ ...editingOrg, name: e.target.value })}
                                    placeholder="Name"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">Alias <span className="text-gray-400 font-normal">(used in email addresses, auto-sanitised)</span></label>
                                  <div className="relative">
                                    <input
                                      type="text"
                                      value={editingOrg.alias || ''}
                                      onChange={(e) => setEditingOrg({ ...editingOrg, alias: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') })}
                                      placeholder="e.g. my_org"
                                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-24 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <span className="absolute right-3 top-2 text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                                      auto+<span className="text-blue-600">{editingOrg.alias || '…'}</span>@catax.ai
                                    </span>
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                                  <textarea
                                    value={editingOrg.description || ''}
                                    onChange={(e) => setEditingOrg({ ...editingOrg, description: e.target.value })}
                                    rows={2}
                                    placeholder="Description"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={editingOrg.is_active}
                                    onChange={(e) => setEditingOrg({ ...editingOrg, is_active: e.target.checked })}
                                    className="rounded"
                                  />
                                  <label className="text-sm text-gray-700">Active</label>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="flex items-center space-x-3">
                                  <p className="text-lg font-medium text-gray-900">{org.name}</p>
                                  {org.alias && (
                                    <span className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{org.alias}</span>
                                  )}
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${org.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                    }`}>
                                    {org.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                                {org.description && (
                                  <p className="text-sm text-gray-600 mt-1">{org.description}</p>
                                )}
                                <p className="text-xs text-gray-400 mt-2">
                                  Members: {org.member_count} | Created: {new Date(org.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            {editingOrg?.id === org.id ? (
                              <>
                                <button
                                  onClick={() => {
                                    handleUpdateOrganization(org.id, {
                                      name: editingOrg.name,
                                      alias: editingOrg.alias,
                                      description: editingOrg.description,
                                      is_active: editingOrg.is_active
                                    });
                                  }}
                                  className="text-green-600 hover:text-green-800 p-2"
                                  title="Save"
                                >
                                  <Save className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setEditingOrg(null)}
                                  className="text-gray-600 hover:text-gray-800 p-2"
                                  title="Cancel"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => setEditingOrg(org)}
                                  className="text-blue-600 hover:text-blue-800 p-2"
                                  title="Edit"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteOrganization(org.id, org.name)}
                                  className="text-red-600 hover:text-red-800 p-2"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => toggleOrgExpansion(org.id)}
                                  className="text-gray-400 hover:text-gray-600 p-2"
                                >
                                  {expandedOrgs.has(org.id) ? (
                                    <ChevronDown className="w-5 h-5" />
                                  ) : (
                                    <ChevronRight className="w-5 h-5" />
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Expanded Members Section */}
                        {expandedOrgs.has(org.id) && (
                          <div className="mt-4 pl-4 border-l-2 border-gray-200">
                            <div className="mb-4">
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">Members</h4>

                              {/* Add Member Form */}
                              <div className="bg-gray-50 p-3 rounded-md mb-3">
                                <p className="text-xs font-medium text-gray-700 mb-2">Add Member</p>
                                <div className="flex gap-2">
                                  <input
                                    type="email"
                                    value={addMemberEmail}
                                    onChange={(e) => setAddMemberEmail(e.target.value)}
                                    placeholder="User email"
                                    className="flex-1 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <select
                                    value={addMemberRole}
                                    onChange={(e) => setAddMemberRole(e.target.value)}
                                    className="text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="member">Member</option>
                                    <option value="admin">Admin</option>
                                  </select>
                                  <button
                                    onClick={() => handleAddMemberToOrg(org.id)}
                                    disabled={!addMemberEmail}
                                    className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                                  >
                                    Add
                                  </button>
                                </div>
                              </div>

                              {/* Members List */}
                              {orgMembers.length > 0 ? (
                                <div className="space-y-2">
                                  {orgMembers.map((member) => (
                                    <div key={member.uid} className="bg-white border border-gray-200 rounded-md p-3 flex items-center justify-between">
                                      <div className="flex items-center space-x-3">
                                        <div>
                                          <p className="text-sm font-medium text-gray-900">{member.name}</p>
                                          <p className="text-xs text-gray-500">{member.email}</p>
                                        </div>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <select
                                          value={member.role}
                                          onChange={(e) => handleUpdateMemberRole(org.id, member.uid, e.target.value)}
                                          className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                          <option value="member">Member</option>
                                          <option value="admin">Admin</option>
                                        </select>
                                        <button
                                          onClick={() => handleRemoveMemberFromOrg(org.id, member.uid, member.name)}
                                          className="text-red-600 hover:text-red-800 p-1"
                                          title="Remove"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500 text-center py-2">No members yet</p>
                              )}
                            </div>

                            {/* Alias */}
                            <div className="mt-4">
                              <h4 className="text-sm font-semibold text-gray-900 mb-2">Email Alias</h4>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <label className="block text-xs font-medium text-gray-500 mb-1">
                                  Alias <span className="font-normal text-gray-400">(used in inbound email address)</span>
                                </label>
                                <div className="flex items-center gap-2">
                                  <div className="relative flex-1">
                                    <input
                                      type="text"
                                      value={orgSettingsMap[org.id]?.alias ?? org.alias ?? ''}
                                      onChange={(e) => setOrgSettingsMap(prev => ({
                                        ...prev,
                                        [org.id]: { ...prev[org.id], alias: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') }
                                      }))}
                                      placeholder="e.g. my_org"
                                      className="w-full border border-gray-300 rounded px-2 py-1.5 pr-44 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <span className="absolute right-2 top-1.5 text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded pointer-events-none">
                                      auto+<span className="text-blue-600">{orgSettingsMap[org.id]?.alias ?? org.alias ?? '…'}</span>@catax.ai
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => handleUpdateOrganization(org.id, { alias: orgSettingsMap[org.id]?.alias ?? org.alias })}
                                    className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 flex items-center gap-1 shrink-0"
                                  >
                                    <Save className="w-3.5 h-3.5" />
                                    Save
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Language & Currency Settings */}
                            <div className="mt-4">
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">Language &amp; Currency</h4>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <div className="flex flex-wrap gap-3 items-end">
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-gray-700">Language</label>
                                    <select
                                      value={orgSettingsMap[org.id]?.language || 'en'}
                                      onChange={(e) => setOrgSettingsMap(prev => ({
                                        ...prev,
                                        [org.id]: { ...prev[org.id], language: e.target.value }
                                      }))}
                                      className="text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value="en">English</option>
                                      <option value="es">Spanish</option>
                                      <option value="es-MX">Spanish (Mexico)</option>
                                      <option value="es-CL">Spanish (Chile)</option>
                                      <option value="es-EC">Spanish (Ecuador)</option>
                                      <option value="pt">Portuguese</option>
                                    </select>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-gray-700">Currency</label>
                                    <select
                                      value={orgSettingsMap[org.id]?.currency || 'USD'}
                                      onChange={(e) => setOrgSettingsMap(prev => ({
                                        ...prev,
                                        [org.id]: { ...prev[org.id], currency: e.target.value }
                                      }))}
                                      className="text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value="USD">USD – US Dollar</option>
                                      <option value="EUR">EUR – Euro</option>
                                      <option value="MXN">MXN – Mexican Peso</option>
                                      <option value="BRL">BRL – Brazilian Real</option>
                                      <option value="GBP">GBP – British Pound</option>
                                      <option value="CAD">CAD – Canadian Dollar</option>
                                      <option value="ARS">ARS – Argentine Peso</option>
                                      <option value="COP">COP – Colombian Peso</option>
                                      <option value="CLP">CLP – Chilean Peso</option>
                                    </select>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-gray-700">Country</label>
                                    <select
                                      value={orgSettingsMap[org.id]?.country || ''}
                                      onChange={(e) => setOrgSettingsMap(prev => ({
                                        ...prev,
                                        [org.id]: { ...prev[org.id], country: e.target.value || null }
                                      }))}
                                      className="text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value="">— Not set —</option>
                                      <option value="US">United States</option>
                                      <option value="GB">United Kingdom</option>
                                      <option value="CA">Canada</option>
                                      <option value="AU">Australia</option>
                                      <option value="DE">Germany</option>
                                      <option value="FR">France</option>
                                      <option value="ES">Spain</option>
                                      <option value="IT">Italy</option>
                                      <option value="NL">Netherlands</option>
                                      <option value="PT">Portugal</option>
                                      <option value="MX">Mexico</option>
                                      <option value="BR">Brazil</option>
                                      <option value="AR">Argentina</option>
                                      <option value="CL">Chile</option>
                                      <option value="CO">Colombia</option>
                                      <option value="PE">Peru</option>
                                      <option value="EC">Ecuador</option>
                                      <option value="IL">Israel</option>
                                      <option value="IN">India</option>
                                      <option value="JP">Japan</option>
                                      <option value="KR">South Korea</option>
                                      <option value="ZA">South Africa</option>
                                    </select>
                                  </div>
                                  <button
                                    onClick={() => handleSaveOrgSettings(org.id)}
                                    disabled={orgSettingsSaving.has(org.id)}
                                    className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                                  >
                                    <Save className="w-3.5 h-3.5" />
                                    {orgSettingsSaving.has(org.id) ? 'Saving...' : 'Save'}
                                  </button>
                                </div>
                                {/* Mission Critical toggle */}
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <label className="flex items-center gap-3 cursor-pointer select-none w-fit">
                                    <input
                                      type="checkbox"
                                      checked={orgSettingsMap[org.id]?.mission_critical ?? false}
                                      onChange={(e) => setOrgSettingsMap(prev => ({
                                        ...prev,
                                        [org.id]: { ...prev[org.id], mission_critical: e.target.checked }
                                      }))}
                                      className="w-4 h-4 accent-orange-600 cursor-pointer"
                                    />
                                    <span className="text-xs font-medium text-gray-700">
                                      Mission Critical
                                    </span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      Priority LLM Queue
                                    </span>
                                  </label>
                                  <p className="mt-1 text-xs text-gray-500 ml-7">
                                    Adds <code className="bg-gray-100 px-1 rounded text-orange-700">X-Vertex-AI-LLM-Shared-Request-Type: priority</code> to all LLM calls for this organisation. Remember to save after toggling.
                                  </p>
                                </div>
                                {/* Daily API call cap */}
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Daily API Call Limit
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      External API
                                    </span>
                                  </label>
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="number"
                                      min={0}
                                      step={1}
                                      placeholder="Unlimited"
                                      value={orgSettingsMap[org.id]?.daily_api_call_limit ?? ''}
                                      onChange={(e) => setOrgSettingsMap(prev => ({
                                        ...prev,
                                        [org.id]: { ...prev[org.id], daily_api_call_limit: e.target.value ? parseInt(e.target.value) : null }
                                      }))}
                                      className="w-32 border border-gray-300 rounded px-2 py-1 text-sm"
                                    />
                                    <span className="text-xs text-gray-500">
                                      calls/day · used today: {orgSettingsMap[org.id]?.api_calls_today ?? 0}
                                    </span>
                                  </div>
                                  <p className="mt-1 text-xs text-gray-500">
                                    Caps this organisation's OAuth API traffic (REST + MCP) per UTC day; over-limit calls receive 429 until midnight UTC. Leave empty for unlimited. Remember to save.
                                  </p>
                                </div>
                                {/* Licence plate → VIN lookup (own Save — separate endpoint) */}
                                <PlateLookupPanel orgId={org.id} showNotification={showNotification} />
                                {/* Usage plan (monthly quotas) */}
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Usage Plan
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      Monthly quotas
                                    </span>
                                  </label>
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <select
                                      value={orgSettingsMap[org.id]?.billing_plan_id ?? ''}
                                      onChange={(e) => setOrgSettingsMap(prev => ({
                                        ...prev,
                                        [org.id]: { ...prev[org.id], billing_plan_id: e.target.value === '' ? null : parseInt(e.target.value) }
                                      }))}
                                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                                    >
                                      <option value="">No plan assigned</option>
                                      {billingPlans
                                        .filter(p => p.is_active || p.id === orgSettingsMap[org.id]?.billing_plan_id)
                                        .map(p => (
                                          <option key={p.id} value={p.id}>
                                            {p.name}{p.is_active ? '' : ' (inactive)'}
                                          </option>
                                        ))}
                                    </select>
                                    <select
                                      value={orgSettingsMap[org.id]?.usage_enforcement ?? 'off'}
                                      onChange={(e) => setOrgSettingsMap(prev => ({
                                        ...prev,
                                        [org.id]: { ...prev[org.id], usage_enforcement: e.target.value }
                                      }))}
                                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                                    >
                                      <option value="off">Enforcement: off (track only)</option>
                                      <option value="warn">Enforcement: warn (banners)</option>
                                      <option value="enforce">Enforcement: enforce (block)</option>
                                    </select>
                                    {orgSettingsMap[org.id]?.plan_id && (
                                      <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                                        legacy plan: {orgSettingsMap[org.id]?.plan_id} (still applies until a plan is assigned)
                                      </span>
                                    )}
                                  </div>
                                  <p className="mt-1 text-xs text-gray-500">
                                    This month: {orgSettingsMap[org.id]?.usage_month?.orders ?? 0} orders
                                    · {orgSettingsMap[org.id]?.usage_month?.part_queries ?? 0} part queries
                                    · {orgSettingsMap[org.id]?.usage_month?.llm_queries ?? 0} LLM queries.
                                    Plans are defined in the Plans tab (terms + caps); staged rollout: assign plan → warn → enforce. Remember to save.
                                  </p>
                                </div>
                                {/* Quote billing agreement (per-org commercial terms) */}
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Quote Agreement Overrides
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                      Billed in arrears
                                    </span>
                                  </label>
                                  <div className="flex items-end gap-3 flex-wrap">
                                    <div>
                                      <span className="block text-[11px] text-gray-500 mb-0.5">Monthly price (USD)</span>
                                      <input
                                        type="number" min="0" step="0.01" placeholder="e.g. 1000"
                                        value={orgSettingsMap[org.id]?.quote_monthly_price_cents != null ? orgSettingsMap[org.id]!.quote_monthly_price_cents! / 100 : ''}
                                        onChange={(e) => setOrgSettingsMap(prev => ({
                                          ...prev,
                                          [org.id]: { ...prev[org.id], quote_monthly_price_cents: e.target.value === '' ? null : Math.round(parseFloat(e.target.value) * 100) }
                                        }))}
                                        className="w-28 border border-gray-300 rounded px-2 py-1 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <span className="block text-[11px] text-gray-500 mb-0.5">Included quotes / month</span>
                                      <input
                                        type="number" min="0" step="1" placeholder="e.g. 1250"
                                        value={orgSettingsMap[org.id]?.quote_included_monthly ?? ''}
                                        onChange={(e) => setOrgSettingsMap(prev => ({
                                          ...prev,
                                          [org.id]: { ...prev[org.id], quote_included_monthly: e.target.value === '' ? null : parseInt(e.target.value) }
                                        }))}
                                        className="w-28 border border-gray-300 rounded px-2 py-1 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <span className="block text-[11px] text-gray-500 mb-0.5">Overage price / quote (USD)</span>
                                      <input
                                        type="number" min="0" step="0.01" placeholder="e.g. 0.80"
                                        value={orgSettingsMap[org.id]?.quote_overage_price_cents != null ? orgSettingsMap[org.id]!.quote_overage_price_cents! / 100 : ''}
                                        onChange={(e) => setOrgSettingsMap(prev => ({
                                          ...prev,
                                          [org.id]: { ...prev[org.id], quote_overage_price_cents: e.target.value === '' ? null : Math.round(parseFloat(e.target.value) * 100) }
                                        }))}
                                        className="w-28 border border-gray-300 rounded px-2 py-1 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <span className="block text-[11px] text-gray-500 mb-0.5">Searches / quote</span>
                                      <input
                                        type="number" min="1" step="1" placeholder="3"
                                        value={orgSettingsMap[org.id]?.quote_allowance_searches ?? ''}
                                        onChange={(e) => setOrgSettingsMap(prev => ({
                                          ...prev,
                                          [org.id]: { ...prev[org.id], quote_allowance_searches: e.target.value === '' ? null : parseInt(e.target.value) }
                                        }))}
                                        className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <span className="block text-[11px] text-gray-500 mb-0.5">Images / quote</span>
                                      <input
                                        type="number" min="1" step="1" placeholder="3"
                                        value={orgSettingsMap[org.id]?.quote_allowance_images ?? ''}
                                        onChange={(e) => setOrgSettingsMap(prev => ({
                                          ...prev,
                                          [org.id]: { ...prev[org.id], quote_allowance_images: e.target.value === '' ? null : parseInt(e.target.value) }
                                        }))}
                                        className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <span className="block text-[11px] text-gray-500 mb-0.5">PDFs / quote</span>
                                      <input
                                        type="number" min="1" step="1" placeholder="1"
                                        value={orgSettingsMap[org.id]?.quote_allowance_pdfs ?? ''}
                                        onChange={(e) => setOrgSettingsMap(prev => ({
                                          ...prev,
                                          [org.id]: { ...prev[org.id], quote_allowance_pdfs: e.target.value === '' ? null : parseInt(e.target.value) }
                                        }))}
                                        className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                                      />
                                    </div>
                                  </div>
                                  <p className="mt-1 text-xs text-gray-500">
                                    This month: {orgSettingsMap[org.id]?.usage_month?.quotes ?? 0} quotes
                                    {(() => {
                                      const s = orgSettingsMap[org.id];
                                      const over = s?.quote_included_monthly != null
                                        ? Math.max(0, (s?.usage_month?.quotes ?? 0) - s.quote_included_monthly!) : 0;
                                      return over > 0 ? ` (${over} over allowance)` : '';
                                    })()}
                                    · {orgSettingsMap[org.id]?.usage_month?.images ?? 0} images
                                    · {orgSettingsMap[org.id]?.usage_month?.pdfs ?? 0} PDFs.
                                    These fields OVERRIDE the assigned plan; empty = use the plan's value (or defaults 3/3/1 for allowances).
                                    No plan and empty price/included = no agreement (usage tracked, nothing billed).
                                    Overage is billed in arrears — usage is never blocked. Remember to save.
                                  </p>
                                </div>
                                  {/* ROI Calculator Settings */}
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <h5 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                                    <TrendingUp className="w-3.5 h-3.5 text-[#1E0DB8]" />
                                    ROI Calculator
                                  </h5>
                                  {/* Master enable toggle */}
                                  <label className="flex items-center gap-3 cursor-pointer select-none w-fit mb-3">
                                    <input
                                      type="checkbox"
                                      checked={orgSettingsMap[org.id]?.roi_calculator_enabled ?? false}
                                      onChange={(e) => setOrgSettingsMap(prev => ({
                                        ...prev,
                                        [org.id]: { ...prev[org.id], roi_calculator_enabled: e.target.checked }
                                      }))}
                                      className="w-4 h-4 accent-[#1E0DB8] cursor-pointer"
                                    />
                                    <span className="text-xs font-medium text-gray-700">Enable ROI Calculator</span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#E5E2FF] text-[#3627BF]">
                                      Org Access
                                    </span>
                                  </label>
                                  {/* B2B / B2C type selector */}
                                  {(orgSettingsMap[org.id]?.roi_calculator_enabled) && (
                                    <div className="flex items-center gap-2 mb-3">
                                      <span className="text-xs font-medium text-gray-600 w-24 flex-shrink-0">Show calculator:</span>
                                      <div className="flex gap-1">
                                        {(['b2b', 'b2c', 'both'] as const).map((mode) => {
                                          const isB2b = mode === 'b2b';
                                          const isBoth = mode === 'both';
                                          const b2bOn = orgSettingsMap[org.id]?.roi_b2b_enabled ?? false;
                                          const b2cOn = orgSettingsMap[org.id]?.roi_b2c_enabled ?? false;
                                          const active = isB2b
                                            ? (b2bOn && !b2cOn)
                                            : isBoth
                                              ? (b2bOn && b2cOn)
                                              : (!b2bOn && b2cOn);
                                          return (
                                            <button
                                              key={mode}
                                              onClick={() => setOrgSettingsMap(prev => ({
                                                ...prev,
                                                [org.id]: {
                                                  ...prev[org.id],
                                                  roi_b2b_enabled: mode === 'b2b' || mode === 'both',
                                                  roi_b2c_enabled: mode === 'b2c' || mode === 'both',
                                                }
                                              }))}
                                              className={`text-[11px] font-semibold px-3 py-1 rounded-md border transition-all ${
                                                active
                                                  ? 'bg-[#1E0DB8] text-white border-[#1E0DB8]'
                                                  : 'bg-white text-gray-500 border-gray-300 hover:border-[#1E0DB8] hover:text-[#1E0DB8]'
                                              }`}
                                            >
                                              {mode === 'b2b' ? 'B2B only' : mode === 'b2c' ? 'B2C only' : 'Both'}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex flex-wrap gap-3 mt-1">
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs font-medium text-gray-700">Default Monthly Quotes</label>
                                      <input
                                        type="number"
                                        placeholder="15000"
                                        value={orgSettingsMap[org.id]?.roi_monthly_quotes ?? ''}
                                        onChange={(e) => setOrgSettingsMap(prev => ({
                                          ...prev,
                                          [org.id]: { ...prev[org.id], roi_monthly_quotes: e.target.value ? parseInt(e.target.value) : null }
                                        }))}
                                        className="w-28 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1E0DB8]"
                                      />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs font-medium text-gray-700">Default FTE Multiplier (×)</label>
                                      <input
                                        type="number"
                                        placeholder="4"
                                        min="1.1"
                                        max="20"
                                        step="0.1"
                                        value={orgSettingsMap[org.id]?.roi_fte_multiplier ?? ''}
                                        onChange={(e) => setOrgSettingsMap(prev => ({
                                          ...prev,
                                          [org.id]: { ...prev[org.id], roi_fte_multiplier: e.target.value ? parseFloat(e.target.value) : null }
                                        }))}
                                        className="w-24 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1E0DB8]"
                                      />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs font-medium text-gray-700">Calculator Currency</label>
                                      <select
                                        value={orgSettingsMap[org.id]?.roi_currency ?? ''}
                                        onChange={(e) => setOrgSettingsMap(prev => ({
                                          ...prev,
                                          [org.id]: { ...prev[org.id], roi_currency: e.target.value || null }
                                        }))}
                                        className="w-28 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1E0DB8]"
                                      >
                                        <option value="">Default (€)</option>
                                        <option value="EUR">EUR — Euro (€)</option>
                                        <option value="USD">USD — US Dollar ($)</option>
                                        <option value="GBP">GBP — British Pound (£)</option>
                                        <option value="MXN">MXN — Mexican Peso</option>
                                        <option value="BRL">BRL — Brazilian Real</option>
                                        <option value="ARS">ARS — Argentine Peso</option>
                                        <option value="CLP">CLP — Chilean Peso</option>
                                        <option value="COP">COP — Colombian Peso</option>
                                        <option value="PEN">PEN — Peruvian Sol</option>
                                        <option value="CAD">CAD — Canadian Dollar</option>
                                        <option value="AUD">AUD — Australian Dollar</option>
                                      </select>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs font-medium text-gray-700">Default FTE Monthly Cost</label>
                                      <input
                                        type="number"
                                        placeholder="3281.68"
                                        min="0"
                                        step="0.01"
                                        value={orgSettingsMap[org.id]?.roi_fte_cost ?? ''}
                                        onChange={(e) => setOrgSettingsMap(prev => ({
                                          ...prev,
                                          [org.id]: { ...prev[org.id], roi_fte_cost: e.target.value ? parseFloat(e.target.value) : null }
                                        }))}
                                        className="w-28 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1E0DB8]"
                                      />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs font-medium text-gray-700">Default Cost per Quote</label>
                                      <input
                                        type="number"
                                        placeholder="0.70"
                                        min="0"
                                        step="0.01"
                                        value={orgSettingsMap[org.id]?.roi_cost_per_quote ?? ''}
                                        onChange={(e) => setOrgSettingsMap(prev => ({
                                          ...prev,
                                          [org.id]: { ...prev[org.id], roi_cost_per_quote: e.target.value ? parseFloat(e.target.value) : null }
                                        }))}
                                        className="w-28 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1E0DB8]"
                                      />
                                    </div>
                                  </div>
                                  {/* B2C Defaults */}
                                  <div className="mt-3 pt-2 border-t border-dashed border-gray-200">
                                    <p className="text-xs font-semibold text-gray-600 mb-2">B2C Calculator Defaults</p>
                                    <div className="flex flex-wrap gap-3">
                                      <div className="flex flex-col gap-1">
                                        <label className="text-xs font-medium text-gray-700">Quotes / month</label>
                                        <input
                                          type="number"
                                          placeholder="10000"
                                          min="0"
                                          step="500"
                                          value={orgSettingsMap[org.id]?.roi_b2c_quotes ?? ''}
                                          onChange={(e) => setOrgSettingsMap(prev => ({
                                            ...prev,
                                            [org.id]: { ...prev[org.id], roi_b2c_quotes: e.target.value ? parseInt(e.target.value) : null }
                                          }))}
                                          className="w-28 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1E0DB8]"
                                        />
                                      </div>
                                      <div className="flex flex-col gap-1">
                                        <label className="text-xs font-medium text-gray-700">Conversion rate (%)</label>
                                        <input
                                          type="number"
                                          placeholder="2.5"
                                          min="0.1"
                                          max="100"
                                          step="0.1"
                                          value={orgSettingsMap[org.id]?.roi_b2c_cr ?? ''}
                                          onChange={(e) => setOrgSettingsMap(prev => ({
                                            ...prev,
                                            [org.id]: { ...prev[org.id], roi_b2c_cr: e.target.value ? parseFloat(e.target.value) : null }
                                          }))}
                                          className="w-28 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1E0DB8]"
                                        />
                                      </div>
                                      <div className="flex flex-col gap-1">
                                        <label className="text-xs font-medium text-gray-700">Order value</label>
                                        <input
                                          type="number"
                                          placeholder="300"
                                          min="0"
                                          step="10"
                                          value={orgSettingsMap[org.id]?.roi_b2c_aov ?? ''}
                                          onChange={(e) => setOrgSettingsMap(prev => ({
                                            ...prev,
                                            [org.id]: { ...prev[org.id], roi_b2c_aov: e.target.value ? parseFloat(e.target.value) : null }
                                          }))}
                                          className="w-28 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1E0DB8]"
                                        />
                                      </div>
                                      <div className="flex flex-col gap-1">
                                        <label className="text-xs font-medium text-gray-700">Gross margin (%)</label>
                                        <input
                                          type="number"
                                          placeholder="60"
                                          min="1"
                                          max="100"
                                          step="1"
                                          value={orgSettingsMap[org.id]?.roi_b2c_gm ?? ''}
                                          onChange={(e) => setOrgSettingsMap(prev => ({
                                            ...prev,
                                            [org.id]: { ...prev[org.id], roi_b2c_gm: e.target.value ? parseFloat(e.target.value) : null }
                                          }))}
                                          className="w-28 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1E0DB8]"
                                        />
                                      </div>
                                      <div className="flex flex-col gap-1">
                                        <label className="text-xs font-medium text-gray-700">CATA pricing mode</label>
                                        <div className="flex rounded border border-gray-300 overflow-hidden w-52">
                                          {(['per-quote', 'pct-order'] as const).map((mode) => {
                                            const active = (orgSettingsMap[org.id]?.roi_b2c_pricing_mode ?? 'per-quote') === mode;
                                            return (
                                              <button
                                                key={mode}
                                                type="button"
                                                onClick={() => setOrgSettingsMap(prev => ({
                                                  ...prev,
                                                  [org.id]: { ...prev[org.id], roi_b2c_pricing_mode: mode }
                                                }))}
                                                className={`flex-1 text-xs font-medium py-1.5 transition-all ${active ? 'bg-[#EEF0FD] text-[#1E0DB8]' : 'bg-white text-gray-500 hover:text-gray-700'}`}
                                              >
                                                {mode === 'per-quote' ? '€ / quote' : '% order'}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                      {(orgSettingsMap[org.id]?.roi_b2c_pricing_mode ?? 'per-quote') === 'per-quote' ? (
                                        <div className="flex flex-col gap-1">
                                          <label className="text-xs font-medium text-gray-700">Cost per quote (€)</label>
                                          <input
                                            type="number"
                                            placeholder="0.50"
                                            min="0"
                                            step="0.05"
                                            value={orgSettingsMap[org.id]?.roi_b2c_cpq ?? ''}
                                            onChange={(e) => setOrgSettingsMap(prev => ({
                                              ...prev,
                                              [org.id]: { ...prev[org.id], roi_b2c_cpq: e.target.value ? parseFloat(e.target.value) : null }
                                            }))}
                                            className="w-28 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1E0DB8]"
                                          />
                                        </div>
                                      ) : (
                                        <div className="flex flex-col gap-1">
                                          <label className="text-xs font-medium text-gray-700">CATA fee (% of order)</label>
                                          <input
                                            type="number"
                                            placeholder="3.0"
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            value={orgSettingsMap[org.id]?.roi_b2c_pct ?? ''}
                                            onChange={(e) => setOrgSettingsMap(prev => ({
                                              ...prev,
                                              [org.id]: { ...prev[org.id], roi_b2c_pct: e.target.value ? parseFloat(e.target.value) : null }
                                            }))}
                                            className="w-28 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1E0DB8]"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <p className="mt-1 text-xs text-gray-500">
                                    Pre-fills the ROI Calculator with org-specific defaults. Members without this enabled cannot access the page. Remember to save after changing.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {organizations.length === 0 && (
                      <div className="p-6 text-center text-gray-500">
                        <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No organizations found</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* System Tab */}
          {!loading && activeTab === 'system' && (
            <div className="space-y-6">
            <AlgorithmSettingsPanel />
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <Server className="w-5 h-5 mr-2" />
                  System Jobs
                </h2>
                <p className="text-sm text-gray-500 mt-1">Trigger background Cloud Run jobs manually.</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between border border-gray-200 rounded-lg p-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Email Scraper</h3>
                    <p className="text-sm text-gray-500">Runs the <code className="text-xs">gmail-listener</code> Cloud Run job to ingest emails.</p>
                  </div>
                  <button
                    onClick={() => handleRunJob('gmail-listener', 'Email Scraper')}
                    disabled={triggeringJob === 'gmail-listener'}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {triggeringJob === 'gmail-listener'
                      ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <Zap className="w-4 h-4" />}
                    Run now
                  </button>
                </div>
                <div className="flex items-center justify-between border border-gray-200 rounded-lg p-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Backfill Parts Summary</h3>
                    <p className="text-sm text-gray-500">Rebuilds the Peças (sol/enc/carrinho) indicator for every order with a linked session, while preserving order history order.</p>
                  </div>
                  <button
                    onClick={() => handleRunJob('parts-summary-backfill', 'Backfill Parts Summary')}
                    disabled={triggeringJob === 'parts-summary-backfill'}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {triggeringJob === 'parts-summary-backfill'
                      ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <Zap className="w-4 h-4" />}
                    Run now
                  </button>
                </div>
              </div>
            </div>
            </div>
          )}

          {/* REMOVED: Health Tab */}
          {!loading && false && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <Server className="w-5 h-5 mr-2" />
                  System Health
                </h2>
                <button
                  onClick={() => fetchSystemHealth()}
                  className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </button>
              </div>

              {healthLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Checking system health...</p>
                </div>
              ) : systemHealth ? (
                <>
                  {/* Overall Status */}
                  <div className={`rounded-lg shadow p-6 ${systemHealth.status === 'healthy' ? 'bg-green-50 border border-green-200' :
                      systemHealth.status === 'degraded' ? 'bg-yellow-50 border border-yellow-200' :
                        'bg-red-50 border border-red-200'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {systemHealth.status === 'healthy' ? (
                          <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                        ) : (
                          <AlertCircle className="w-8 h-8 text-yellow-600 mr-3" />
                        )}
                        <div>
                          <h3 className="text-lg font-bold capitalize">{systemHealth.status}</h3>
                          <p className="text-sm text-gray-600">
                            Environment: <strong>{systemHealth.environment}</strong> | Version: <strong>{systemHealth.version}</strong>
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(systemHealth.timestamp * 1000).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>

                  {/* Health Checks Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Object.entries(systemHealth.checks || {}).map(([key, check]: [string, any]) => (
                      <div key={key} className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-medium text-gray-900 capitalize">{key.replace(/_/g, ' ')}</h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${check.status === 'healthy' ? 'bg-green-100 text-green-800' :
                              check.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                check.status === 'unhealthy' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                            }`}>
                            {check.status}
                          </span>
                        </div>
                        {check.message && <p className="text-sm text-gray-600 mb-2">{check.message}</p>}
                        {/* Show system metrics */}
                        {key === 'system' && (
                          <div className="space-y-2 text-sm">
                            {check.python_version && <p><span className="text-gray-500">Python:</span> {check.python_version}</p>}
                            {check.memory_used_mb && <p><span className="text-gray-500">Memory:</span> {check.memory_used_mb} MB ({check.memory_percent}%)</p>}
                            {check.cpu_percent !== undefined && <p><span className="text-gray-500">CPU:</span> {check.cpu_percent}%</p>}
                            {check.uptime_seconds && <p><span className="text-gray-500">Uptime:</span> {Math.round(check.uptime_seconds / 3600)}h {Math.round((check.uptime_seconds % 3600) / 60)}m</p>}
                          </div>
                        )}
                        {/* Show env vars */}
                        {key === 'env_vars' && check.variables && (
                          <div className="space-y-1 text-sm">
                            {Object.entries(check.variables).map(([varName, status]: [string, any]) => (
                              <div key={varName} className="flex items-center justify-between">
                                <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{varName}</code>
                                <span className={status === 'set' ? 'text-green-600' : 'text-red-600'}>{status}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <Server className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No health data available</p>
                </div>
              )}
            </div>
          )}

          {/* Feedback Tab */}
          {!loading && activeTab === 'feedback' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <Star className="w-5 h-5 mr-2" />
                  Feedback Dashboard
                </h2>
                <button
                  onClick={() => fetchFeedback()}
                  className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </button>
              </div>

              {feedbackLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading feedback...</p>
                </div>
              ) : (
                <>
                  {/* Summary Cards */}
                  {feedbackSummary && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                          <div className="p-3 rounded-full bg-yellow-100">
                            <Star className="w-6 h-6 text-yellow-600" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Overall Rating</p>
                            <p className="text-2xl font-bold text-gray-900">{feedbackSummary.overall_average}/5</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                          <div className="p-3 rounded-full bg-blue-100">
                            <MessageSquare className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Session Feedback</p>
                            <p className="text-2xl font-bold text-gray-900">{feedbackSummary.session_feedback?.total || 0}</p>
                            <p className="text-xs text-gray-400">Avg: {feedbackSummary.session_feedback?.average_rating || 0}/5</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                          <div className="p-3 rounded-full bg-green-100">
                            <ThumbsUp className="w-6 h-6 text-green-600" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Helpful</p>
                            <p className="text-2xl font-bold text-gray-900">{feedbackSummary.message_feedback?.helpful || 0}</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                          <div className="p-3 rounded-full bg-red-100">
                            <ThumbsDown className="w-6 h-6 text-red-600" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Not Helpful</p>
                            <p className="text-2xl font-bold text-gray-900">{feedbackSummary.message_feedback?.not_helpful || 0}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rating Distribution */}
                  {feedbackSummary?.rating_distribution && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">Rating Distribution</h3>
                      <div className="space-y-2">
                        {[5, 4, 3, 2, 1].map(rating => {
                          const count = feedbackSummary.rating_distribution[rating] || 0;
                          const total = Object.values(feedbackSummary.rating_distribution as Record<string, number>).reduce((a: number, b: number) => a + b, 0);
                          const pct = total > 0 ? (count / total) * 100 : 0;
                          return (
                            <div key={rating} className="flex items-center gap-3">
                              <span className="text-sm w-8">{rating}★</span>
                              <div className="flex-1 bg-gray-200 rounded-full h-3">
                                <div className="bg-yellow-400 h-3 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-sm text-gray-600 w-12 text-right">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Recent Feedback */}
                  <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-700">Recent Feedback</h3>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {recentFeedback.map((fb, idx) => (
                        <div key={idx} className="px-6 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${fb.type === 'session' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                                }`}>
                                {fb.type}
                              </span>
                              <span className="text-sm font-medium text-gray-900">{fb.user_name}</span>
                              <span className="text-sm text-gray-500">{fb.user_email}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-yellow-500">{'★'.repeat(fb.rating)}{'☆'.repeat(5 - fb.rating)}</span>
                              {fb.helpful !== undefined && (
                                fb.helpful
                                  ? <ThumbsUp className="w-4 h-4 text-green-500" />
                                  : <ThumbsDown className="w-4 h-4 text-red-500" />
                              )}
                            </div>
                          </div>
                          {fb.comments && (
                            <p className="mt-2 text-sm text-gray-600 italic">"{fb.comments}"</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">{fb.created_at ? new Date(fb.created_at).toLocaleString() : ''}</p>
                        </div>
                      ))}
                      {recentFeedback.length === 0 && (
                        <div className="px-6 py-8 text-center text-gray-500">
                          <Star className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                          <p>No feedback yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* REMOVED: Orders Tab */}
          {!loading && false && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Orders Overview
                </h2>
                <button
                  onClick={() => fetchAllOrders()}
                  className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </button>
              </div>

              {ordersLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading orders...</p>
                </div>
              ) : (
                <>
                  {/* Status Distribution */}
                  {ordersStats && Object.keys(ordersStats).length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {Object.entries(ordersStats).map(([status, count]: [string, any]) => (
                        <div key={status} className="bg-white rounded-lg shadow p-4 text-center">
                          <p className="text-2xl font-bold text-gray-900">{count}</p>
                          <p className="text-sm text-gray-600 capitalize">{status.replace(/_/g, ' ')}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Orders Table */}
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">VIN</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parts</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {allOrders.map((order) => (
                            <tr key={order.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">#{order.id}</td>
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900">{order.user_name}</div>
                                <div className="text-xs text-gray-500">{order.user_email}</div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm text-gray-900">{order.name}</div>
                                <div className="text-xs text-gray-500">{order.service_type}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 font-mono">{order.vin}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{order.parts_count}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${order.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                      order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-800'
                                  }`}>
                                  {order.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">
                                {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {allOrders.length === 0 && (
                      <div className="px-6 py-8 text-center text-gray-500">
                        <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No orders found</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* REMOVED: Assistance Requests Tab */}
          {!loading && false && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <HelpCircle className="w-5 h-5 mr-2" />
                  Assistance Requests
                </h2>
                <button
                  onClick={() => fetchAssistanceRequests()}
                  className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </button>
              </div>

              {assistanceLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading assistance requests...</p>
                </div>
              ) : (
                <>
                  {/* Stats Row */}
                  {assistanceStats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(assistanceStats.status_distribution || {}).map(([status, count]: [string, any]) => (
                        <div key={status} className={`rounded-lg shadow p-4 text-center ${status === 'pending' ? 'bg-yellow-50 border border-yellow-200' :
                            status === 'in_progress' ? 'bg-blue-50 border border-blue-200' :
                              status === 'resolved' ? 'bg-green-50 border border-green-200' :
                                'bg-gray-50 border border-gray-200'
                          }`}>
                          <p className="text-2xl font-bold text-gray-900">{count}</p>
                          <p className="text-sm text-gray-600 capitalize">{status.replace(/_/g, ' ')}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Requests List */}
                  <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
                    {assistanceRequests.map((req) => (
                      <div key={req.id} className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${req.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                                  req.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                    req.priority === 'normal' ? 'bg-blue-100 text-blue-800' :
                                      'bg-gray-100 text-gray-800'
                                }`}>
                                {req.priority}
                              </span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  req.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                    req.status === 'resolved' ? 'bg-green-100 text-green-800' :
                                      'bg-gray-100 text-gray-800'
                                }`}>
                                {req.status}
                              </span>
                              <h3 className="text-sm font-medium text-gray-900">{req.subject}</h3>
                            </div>
                            <p className="mt-1 text-sm text-gray-600">{req.description}</p>
                            <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                              <span>From: {req.user_name} ({req.user_email})</span>
                              <span>Order: #{req.order_id}</span>
                              <span>{req.created_at ? new Date(req.created_at).toLocaleString() : ''}</span>
                            </div>
                            {req.resolution_notes && (
                              <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-800">
                                <strong>Resolution:</strong> {req.resolution_notes}
                              </div>
                            )}
                          </div>
                          <div className="ml-4 flex items-center space-x-2">
                            {req.status === 'pending' && (
                              <button
                                onClick={async () => {
                                  try {
                                    await adminService.updateAssistanceRequestStatus(req.id, 'in_progress');
                                    fetchAssistanceRequests();
                                    showNotification('Success', 'Request marked as in progress', 'success');
                                  } catch (err) {
                                    showNotification('Error', 'Failed to update status', 'error');
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-200 rounded"
                              >
                                Start
                              </button>
                            )}
                            {(req.status === 'pending' || req.status === 'in_progress') && (
                              <button
                                onClick={async () => {
                                  const notes = prompt('Resolution notes (optional):');
                                  try {
                                    await adminService.updateAssistanceRequestStatus(req.id, 'resolved', notes || undefined);
                                    fetchAssistanceRequests();
                                    showNotification('Success', 'Request resolved', 'success');
                                  } catch (err) {
                                    showNotification('Error', 'Failed to resolve request', 'error');
                                  }
                                }}
                                className="text-green-600 hover:text-green-800 text-sm px-3 py-1 border border-green-200 rounded"
                              >
                                Resolve
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {assistanceRequests.length === 0 && (
                      <div className="px-6 py-8 text-center text-gray-500">
                        <HelpCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No assistance requests</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* REMOVED: Feature Flags Tab */}
          {!loading && activeTab === 'feature-flags' && (
            <div className="space-y-8">

              {/* ── Global Feature Flags ── */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                      <ToggleRight className="w-5 h-5 text-blue-500" />
                      Global Feature Flags
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">System-wide toggles that apply to all organizations.</p>
                  </div>
                  <button
                    onClick={() => fetchFeatureFlags()}
                    className="text-blue-600 hover:text-blue-800 flex items-center text-sm gap-1"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </button>
                </div>

                {flagsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading feature flags...</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {featureFlags.map((flag, idx) => (
                      <div key={idx} className="px-6 py-4 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="text-sm font-medium text-gray-900">{flag.flag_name}</h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${flag.is_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                              {flag.is_enabled ? 'ON' : 'OFF'}
                            </span>
                          </div>
                          {flag.description && <p className="mt-0.5 text-sm text-gray-500">{flag.description}</p>}
                          {flag.updated_by && (
                            <p className="mt-0.5 text-xs text-gray-400">
                              Updated by {flag.updated_by}{flag.updated_at ? ` · ${new Date(flag.updated_at).toLocaleString()}` : ''}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              await adminService.updateFeatureFlag(flag.flag_name, !flag.is_enabled);
                              fetchFeatureFlags();
                              showNotification('Success', `${flag.flag_name} ${!flag.is_enabled ? 'enabled' : 'disabled'}`, 'success');
                            } catch (err) {
                              showNotification('Error', 'Failed to update feature flag', 'error');
                            }
                          }}
                          className="ml-4 flex-shrink-0"
                          title={flag.is_enabled ? 'Disable' : 'Enable'}
                        >
                          {flag.is_enabled
                            ? <ToggleRight className="w-10 h-10 text-green-500 hover:text-green-700 transition-colors" />
                            : <ToggleLeft className="w-10 h-10 text-gray-300 hover:text-gray-500 transition-colors" />}
                        </button>
                      </div>
                    ))}
                    {featureFlags.length === 0 && (
                      <div className="px-6 py-10 text-center text-gray-400">
                        <ToggleLeft className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">No global feature flags configured yet.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Organization-Scoped Feature Flags ── */}
              <OrgFeatureFlagsPanel
                organizations={organizations}
                onFetchOrgs={() => {
                  if (organizations.length === 0) fetchOrganizations();
                }}
                adminService={adminService}
                showNotification={showNotification}
              />
            </div>
          )}

          {/* Catalog Knowledge Tab */}
          {!loading && activeTab === 'knowledge' && (
            <KnowledgeRulesTab />
          )}

          {/* Knowledge Examples (flywheel review queue) Tab */}
          {!loading && activeTab === 'knowledge-examples' && (
            <KnowledgeExamplesTab />
          )}

          {/* Redis Tab */}
          {!loading && activeTab === 'redis' && (
            <RedisTab />
          )}

          {/* WMI Manufacturers Tab */}
          {!loading && activeTab === 'wmi' && (
            <WmiTab />
          )}

          {/* Pick memory: what purchase history and people taught the autopilot */}
          {!loading && activeTab === 'memory' && (
            <MemoryTab />
          )}

          {/* System Messages Tab */}
          {!loading && activeTab === 'messages' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                      Customer Messages
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Broadcast banners to customers — e.g. "Catalog XX is down", "System issue being handled".
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={fetchSystemMessages}
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      Refresh
                    </button>
                    <button
                      onClick={() => {
                        setEditingMessage(null);
                        setMessageForm({ title: '', message: '', message_type: 'warning', is_active: true, organization_id: null });
                        setShowCreateMessage(true);
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700"
                    >
                      <Plus className="w-4 h-4" />
                      New Message
                    </button>
                  </div>
                </div>

                {/* Create / Edit Form */}
                {showCreateMessage && (
                  <div className="px-6 py-5 border-b border-amber-100 bg-amber-50">
                    <h3 className="text-sm font-semibold text-gray-800 mb-4">
                      {editingMessage ? 'Edit Message' : 'Create New Message'}
                    </h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
                        <input
                          type="text"
                          value={messageForm.title}
                          onChange={e => setMessageForm(f => ({ ...f, title: e.target.value }))}
                          placeholder="e.g. Catalog XX is temporarily unavailable"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Message *</label>
                        <textarea
                          rows={3}
                          value={messageForm.message}
                          onChange={e => setMessageForm(f => ({ ...f, message: e.target.value }))}
                          placeholder="Provide details about the issue or announcement…"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                        <select
                          value={messageForm.message_type}
                          onChange={e => setMessageForm(f => ({ ...f, message_type: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                        >
                          <option value="info">ℹ️ Info</option>
                          <option value="warning">⚠️ Warning</option>
                          <option value="error">🔴 Error / Outage</option>
                          <option value="success">✅ Success / Resolved</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Organization (leave blank = global)</label>
                        <select
                          value={messageForm.organization_id ?? ''}
                          onChange={e => setMessageForm(f => ({ ...f, organization_id: e.target.value ? Number(e.target.value) : null }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                        >
                          <option value="">— All organizations (global) —</option>
                          {organizations.map(org => (
                            <option key={org.id} value={org.id}>{org.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center gap-2 sm:col-span-2">
                        <button
                          type="button"
                          onClick={() => setMessageForm(f => ({ ...f, is_active: !f.is_active }))}
                          className="flex items-center gap-1 text-sm"
                        >
                          {messageForm.is_active
                            ? <ToggleRight className="w-8 h-8 text-green-500" />
                            : <ToggleLeft className="w-8 h-8 text-gray-300" />}
                          <span className={messageForm.is_active ? 'text-green-700 font-medium' : 'text-gray-500'}>
                            {messageForm.is_active ? 'Active (shown to users)' : 'Inactive (hidden)'}
                          </span>
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={handleSaveMessage}
                        disabled={messageSaving || !messageForm.title.trim() || !messageForm.message.trim()}
                        className="px-4 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {messageSaving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                        <Save className="w-3.5 h-3.5" />
                        {editingMessage ? 'Save Changes' : 'Create Message'}
                      </button>
                      <button
                        onClick={() => { setShowCreateMessage(false); setEditingMessage(null); }}
                        className="px-4 py-1.5 text-sm border border-gray-300 text-gray-600 rounded-md hover:bg-gray-100"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Messages list */}
                {systemMessagesLoading ? (
                  <div className="py-10 text-center">
                    <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-amber-600 mx-auto" />
                    <p className="mt-2 text-sm text-gray-500">Loading messages…</p>
                  </div>
                ) : systemMessages.length === 0 ? (
                  <div className="py-12 text-center text-gray-400">
                    <AlertCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No messages configured. Click <strong>New Message</strong> to create one.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {systemMessages.map((msg: any) => {
                      const typeConfig: Record<string, { bg: string; border: string; badge: string; icon: string }> = {
                        info:    { bg: 'bg-blue-50',   border: 'border-blue-200',  badge: 'bg-blue-100 text-blue-800',   icon: 'ℹ️' },
                        warning: { bg: 'bg-amber-50',  border: 'border-amber-200', badge: 'bg-amber-100 text-amber-800', icon: '⚠️' },
                        error:   { bg: 'bg-red-50',    border: 'border-red-200',   badge: 'bg-red-100 text-red-800',     icon: '🔴' },
                        success: { bg: 'bg-green-50',  border: 'border-green-200', badge: 'bg-green-100 text-green-800', icon: '✅' },
                      };
                      const tc = typeConfig[msg.message_type] ?? typeConfig.info;
                      return (
                        <div key={msg.id} className={`px-6 py-4 flex items-start justify-between gap-4 ${!msg.is_active ? 'opacity-50' : ''}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="text-base">{tc.icon}</span>
                              <span className="text-sm font-semibold text-gray-900">{msg.title}</span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tc.badge}`}>
                                {msg.message_type}
                              </span>
                              {msg.organization_id ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                  {organizations.find(o => o.id === msg.organization_id)?.name ?? `Org #${msg.organization_id}`}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Global</span>
                              )}
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${msg.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                                {msg.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 whitespace-pre-line">{msg.message}</p>
                            {msg.created_by && (
                              <p className="mt-1 text-xs text-gray-400">
                                Created by {msg.created_by}
                                {msg.created_at ? ` · ${new Date(msg.created_at).toLocaleString()}` : ''}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleToggleMessage(msg)}
                              title={msg.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {msg.is_active
                                ? <ToggleRight className="w-8 h-8 text-green-500 hover:text-green-700 transition-colors" />
                                : <ToggleLeft className="w-8 h-8 text-gray-300 hover:text-gray-500 transition-colors" />}
                            </button>
                            <button
                              onClick={() => openEditMessage(msg)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteMessage(msg)}
                              className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* LLM Cost Tab */}
          {!loading && activeTab === 'llm-costs' && (
            <LLMCostDashboard />
          )}

          {/* Parts Operations Dashboard Tab */}
          {!loading && activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* Controls bar */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex-1 min-w-[180px]">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Organisation</label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={dashboardOrgId ?? ''}
                      onChange={e => {
                        const id = e.target.value ? Number(e.target.value) : null;
                        setDashboardOrgId(id);
                        setDashboardData(null);
                        if (id) fetchOrgDashboard(id);
                      }}
                    >
                      <option value="">— select an organisation —</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Start date</label>
                    <input type="date" value={dashboardStartDate}
                      onChange={e => setDashboardStartDate(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">End date</label>
                    <input type="date" value={dashboardEndDate}
                      onChange={e => setDashboardEndDate(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                  <div className="w-36">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Assumed $/part</label>
                    <input type="number" min={0} value={dashboardAssumedPrice}
                      onChange={e => setDashboardAssumedPrice(Number(e.target.value))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                  <button
                    disabled={!dashboardOrgId || dashboardLoading}
                    onClick={() => dashboardOrgId && fetchOrgDashboard(dashboardOrgId)}
                    className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-2"
                  >
                    {dashboardLoading
                      ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Loading…</>
                      : <><RefreshCw className="w-4 h-4" /> Run</>}
                  </button>
                </div>
                {dashboardData && (
                  <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
                    ⚠ Revenue figures use <strong>${dashboardData.assumed_price_per_part}</strong> per loaded part (working assumption).
                    Replace with real pricing data once catalog integration is live.
                  </p>
                )}
              </div>

              {dashboardLoading && (
                <div className="flex justify-center py-20">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
                    <p className="mt-3 text-sm text-gray-500">Running queries…</p>
                  </div>
                </div>
              )}

              {!dashboardLoading && dashboardData && (() => {
                const d = dashboardData;
                const kpi = d.pulse_kpis || {};
                const pipe = d.session_pipeline || {};
                const vel = d.session_velocity || {};
                const pf = d.parts_funnel || {};
                const ag = d.agent_metrics || {};
                const daily: any[] = d.daily_activity || [];
                const cats: any[] = d.catalog_mix || [];
                const models: any[] = d.model_mix || [];
                const parts: any[] = d.top_parts || [];
                const users: any[] = d.user_concentration || [];

                const pct = (n: any, d: any) => d && d > 0 ? `${Math.round((n / d) * 100)}%` : '—';
                const fmtNum = (n: any) => n == null ? '—' : Number(n).toLocaleString();
                const fmtPct = (n: any) => n == null ? '—' : `${(Number(n) * 100).toFixed(1)}%`;
                const fmtUsd = (n: any) => n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
                const fmtDur = (secs: any) => {
                  if (secs == null) return '—';
                  const s = Number(secs);
                  if (s < 60) return `${s}s`;
                  return `${Math.floor(s / 60)}m ${s % 60}s`;
                };

                const kpiTiles = [
                  { label: 'Sessions', value: fmtNum(kpi.total_sessions), color: 'bg-blue-50 text-blue-900' },
                  { label: 'Unique users', value: fmtNum(kpi.unique_users), color: 'bg-indigo-50 text-indigo-900' },
                  { label: 'Sessions with parts loaded', value: fmtNum(kpi.sessions_with_parts_loaded), sub: pct(kpi.sessions_with_parts_loaded, kpi.total_sessions), color: 'bg-teal-50 text-teal-900' },
                  { label: 'Total parts loaded', value: fmtNum(kpi.total_parts_loaded), color: 'bg-teal-50 text-teal-900' },
                  { label: 'Pilot part revenue', value: fmtUsd(kpi.pilot_part_revenue_usd), sub: `@ $${d.assumed_price_per_part}/part`, color: 'bg-emerald-50 text-emerald-900' },
                  { label: 'Avg match confidence', value: kpi.avg_match_confidence != null ? `${(Number(kpi.avg_match_confidence) * 100).toFixed(1)}%` : '—', color: 'bg-violet-50 text-violet-900' },
                ];

                const pipeStages = [
                  { label: 'Inquiry started', n: pipe.s1_inquiry_started, total: pipe.s1_inquiry_started },
                  { label: 'VIN decoded', n: pipe.s2_vin_decoded, total: pipe.s1_inquiry_started },
                  { label: 'Parts validated', n: pipe.s3_parts_validated, total: pipe.s1_inquiry_started },
                  { label: 'Parts loaded ✓', n: pipe.s4_parts_loaded, total: pipe.s1_inquiry_started },
                ];

                const pfStages = [
                  { label: 'Parts searched', n: pf.p1_parts_searched, total: pf.p1_parts_searched },
                  { label: 'Parts validated', n: pf.p2_parts_validated, total: pf.p1_parts_searched },
                  { label: 'Parts loaded ✓', n: pf.p3_parts_loaded, total: pf.p1_parts_searched },
                ];

                const maxDaily = Math.max(...daily.map((r: any) => r.sessions_count || 0), 1);
                const maxCat = Math.max(...cats.map((r: any) => r.session_count || 0), 1);
                const maxUser = Math.max(...users.map((r: any) => r.session_count || 0), 1);

                const agentAlerts = [
                  { label: 'Search-agent retries', value: fmtNum(ag.total_retries), ok: true },
                  { label: 'Avg confidence', value: ag.avg_match_confidence != null ? `${(Number(ag.avg_match_confidence) * 100).toFixed(1)}%` : '—', ok: true },
                  { label: 'Complementary suggestions', value: fmtNum(ag.total_complementary_suggestions), ok: true },
                  { label: 'Catalog match rate', value: fmtPct(ag.catalog_match_rate), ok: true },
                  { label: 'Fallback matches', value: fmtNum(ag.fallback_match_sessions), ok: Number(ag.fallback_match_sessions || 0) === 0 },
                  { label: 'Vision-agent sessions', value: fmtNum(ag.vision_agent_sessions), ok: null },
                  { label: 'Repair instruction outputs', value: fmtNum(ag.sessions_with_repair_instructions), ok: null },
                  { label: 'Streaming errors', value: fmtNum(ag.streaming_error_sessions), ok: Number(ag.streaming_error_sessions || 0) === 0 },
                ];

                return (
                  <>
                    {/* §4 Pulse KPIs */}
                    <div>
                      <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Pilot Pulse</h2>
                      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
                        {kpiTiles.map((t, i) => (
                          <div key={i} className={`rounded-xl p-4 ${t.color}`}>
                            <p className="text-xs font-medium opacity-70 mb-1">{t.label}</p>
                            <p className="text-2xl font-semibold tabular-nums">{t.value}</p>
                            {t.sub && <p className="text-xs opacity-60 mt-0.5">{t.sub}</p>}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* §5a Session Pipeline */}
                      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-sm font-semibold text-gray-700 mb-4">Session Pipeline</h3>
                        <div className="space-y-3">
                          {pipeStages.map((s, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <span className="w-36 text-xs text-gray-600 shrink-0">{s.label}</span>
                              <div className="flex-1 bg-gray-100 rounded-full h-3">
                                <div className="bg-blue-500 h-3 rounded-full transition-all"
                                  style={{ width: `${s.total ? Math.round((s.n / s.total) * 100) : 0}%` }} />
                              </div>
                              <span className="w-8 text-xs font-medium tabular-nums text-right">{fmtNum(s.n)}</span>
                              <span className="w-14 text-xs text-gray-400 tabular-nums text-right">{pct(s.n, s.total)}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* §5b Operational Velocity */}
                      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-sm font-semibold text-gray-700 mb-4">Operational Velocity</h3>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { label: 'Median duration', value: fmtDur(vel.mean_duration_seconds) },
                            { label: 'Mean active duration', value: fmtDur(vel.mean_active_duration_seconds) },
                            { label: 'Off-hours sessions', value: fmtNum(vel.off_hours_sessions) },
                            { label: 'Weekend sessions', value: fmtNum(vel.weekend_sessions) },
                            { label: 'Sessions >30 min', value: fmtNum(vel.long_sessions) },
                            { label: 'Avg agent calls/session', value: vel.avg_search_agent_calls != null ? Number(vel.avg_search_agent_calls).toFixed(1) : '—' },
                          ].map((t, i) => (
                            <div key={i} className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500 mb-0.5">{t.label}</p>
                              <p className="text-lg font-semibold tabular-nums text-gray-800">{t.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* §6 Parts Funnel */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">Parts Funnel</h3>
                      <div className="space-y-3">
                        {pfStages.map((s, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <span className="w-36 text-xs text-gray-600 shrink-0">{s.label}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-3">
                              <div className="bg-teal-500 h-3 rounded-full transition-all"
                                style={{ width: `${s.total ? Math.round((s.n / s.total) * 100) : 0}%` }} />
                            </div>
                            <span className="w-8 text-xs font-medium tabular-nums text-right">{fmtNum(s.n)}</span>
                            <span className="w-14 text-xs text-gray-400 tabular-nums text-right">{pct(s.n, s.total)}</span>
                          </div>
                        ))}
                      </div>
                      {pf.p3_revenue_usd != null && (
                        <p className="mt-4 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                          Loaded parts value: <strong>{fmtUsd(pf.p3_revenue_usd)}</strong> (@ ${d.assumed_price_per_part}/part)
                        </p>
                      )}
                    </div>

                    {/* §7 Daily Activity */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">Daily Activity</h3>
                      {daily.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-6">No sessions in this date range</p>
                      ) : (
                        <div className="flex items-end gap-2 h-28">
                          {daily.map((row: any, i: number) => (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1">
                              <span className="text-xs font-medium text-gray-600">{row.sessions_count}</span>
                              <div
                                className={`w-full rounded-t transition-all ${row.sessions_count > 0 ? 'bg-blue-400' : 'bg-gray-200'}`}
                                style={{ height: `${Math.max(4, Math.round((row.sessions_count / maxDaily) * 80))}px` }}
                                title={`${row.session_date}: ${row.sessions_count} sessions`}
                              />
                              <span className="text-[10px] text-gray-400 truncate w-full text-center">
                                {new Date(row.session_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* §8a Catalog Mix */}
                      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-sm font-semibold text-gray-700 mb-4">Sessions by Catalog</h3>
                        <div className="space-y-2">
                          {cats.map((r: any, i: number) => (
                            <div key={i} className="flex items-center gap-3">
                              <span className="w-28 text-xs text-gray-600 shrink-0 truncate">{r.catalog}</span>
                              <div className="flex-1 bg-gray-100 rounded-full h-2.5">
                                <div className="bg-indigo-400 h-2.5 rounded-full"
                                  style={{ width: `${Math.round((r.session_count / maxCat) * 100)}%` }} />
                              </div>
                              <span className="text-xs font-medium tabular-nums text-gray-700 w-6 text-right">{r.session_count}</span>
                            </div>
                          ))}
                          {cats.length === 0 && <p className="text-xs text-gray-400">No data</p>}
                        </div>
                      </div>

                      {/* §9 User Concentration */}
                      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-sm font-semibold text-gray-700 mb-4">User Concentration</h3>
                        <div className="space-y-2">
                          {users.map((r: any, i: number) => (
                            <div key={i} className="flex items-center gap-3">
                              <span className="w-28 text-xs text-gray-600 shrink-0 truncate">{r.user_label}</span>
                              <div className="flex-1 bg-gray-100 rounded-full h-2.5">
                                <div className="bg-teal-400 h-2.5 rounded-full"
                                  style={{ width: `${Math.round((r.session_count / maxUser) * 100)}%` }} />
                              </div>
                              <span className="text-xs font-medium tabular-nums text-gray-700 w-6 text-right">{r.session_count}</span>
                            </div>
                          ))}
                          {users.length === 0 && <p className="text-xs text-gray-400">No data</p>}
                        </div>
                      </div>
                    </div>

                    {/* §8b Top Vehicle Models */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">Top Vehicle Models</h3>
                      {models.length === 0
                        ? <p className="text-sm text-gray-400">No VIN-decoded sessions in this range</p>
                        : <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead><tr className="text-xs text-gray-400 border-b border-gray-100">
                                <th className="pb-2 text-left font-medium">Model</th>
                                <th className="pb-2 text-left font-medium">Make</th>
                                <th className="pb-2 text-right font-medium">Sessions</th>
                              </tr></thead>
                              <tbody className="divide-y divide-gray-50">
                                {models.map((r: any, i: number) => (
                                  <tr key={i} className="hover:bg-gray-50">
                                    <td className="py-1.5 text-gray-800">{r.model_name}</td>
                                    <td className="py-1.5 text-gray-500 text-xs">{r.make}</td>
                                    <td className="py-1.5 text-right font-medium tabular-nums">{r.session_count}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>}
                    </div>

                    {/* §8c Top Part Numbers */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">Top Part Numbers</h3>
                      {parts.length === 0
                        ? <p className="text-sm text-gray-400">No loaded parts in this range</p>
                        : <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead><tr className="text-xs text-gray-400 border-b border-gray-100">
                                <th className="pb-2 text-left font-medium">OE Number</th>
                                <th className="pb-2 text-left font-medium">Description</th>
                                <th className="pb-2 text-left font-medium">Catalog</th>
                                <th className="pb-2 text-right font-medium">Loads</th>
                              </tr></thead>
                              <tbody className="divide-y divide-gray-50">
                                {parts.map((r: any, i: number) => (
                                  <tr key={i} className="hover:bg-gray-50">
                                    <td className="py-1.5 font-mono text-xs text-blue-700">{r.oe_number}</td>
                                    <td className="py-1.5 text-gray-700 max-w-xs truncate">{r.description || '—'}</td>
                                    <td className="py-1.5 text-gray-500 text-xs">{r.catalog || '—'}</td>
                                    <td className="py-1.5 text-right font-medium tabular-nums">{r.load_count}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>}
                    </div>

                    {/* §10 Agent Activity & Exceptions */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">Agent Activity &amp; Exceptions</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {agentAlerts.map((a, i) => {
                          const bgClass = a.ok === true
                            ? 'bg-green-50 border-green-100 text-green-900'
                            : a.ok === false
                              ? 'bg-red-50 border-red-100 text-red-900'
                              : 'bg-amber-50 border-amber-100 text-amber-900';
                          return (
                            <div key={i} className={`rounded-lg border p-3 ${bgClass}`}>
                              <p className="text-xs opacity-70 mb-0.5">{a.label}</p>
                              <p className="text-xl font-semibold tabular-nums">{a.value}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                );
              })()}

              {!dashboardLoading && !dashboardData && dashboardOrgId && (
                <div className="text-center py-16 text-gray-400">
                  <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Select a date range and click <strong>Run</strong> to load the dashboard.</p>
                </div>
              )}

              {!dashboardOrgId && (
                <div className="text-center py-16 text-gray-400">
                  <BarChart2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Select an organisation above to view its parts operations dashboard.</p>
                </div>
              )}
            </div>
          )}

          {/* Version indicator */}
          <div className="mt-8 pt-4 border-t border-gray-200 text-center">
            <VersionIndicator className="text-gray-400" showIcon={true} size="medium" />
          </div>
        </div>

        {/* Add User Modal */}
        {showAddUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Add New User</h3>
                <button
                  onClick={() => {
                    setShowAddUser(false);
                    setNewUserData({ name: '', middle_name: '', last_name: '', email: '', password: '', role: 'user', send_welcome_email: true, welcome_email_locale: 'en', organization_id: null, organization_role: 'member' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={newUserData.name}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="e.g. John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
                  <input
                    type="text"
                    value={newUserData.middle_name}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, middle_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="e.g. Michael"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={newUserData.last_name}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, last_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="e.g. Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="e.g. john@company.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Temporary Password *</label>
                  <input
                    type="text"
                    value={newUserData.password}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm font-mono focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="At least 6 characters"
                    autoComplete="new-password"
                  />
                  <p className="text-xs text-gray-500 mt-1.5">
                    This password creates the sign-in account and is included in the welcome email when enabled.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={newUserData.role}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="send_welcome_email"
                    checked={newUserData.send_welcome_email}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, send_welcome_email: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="send_welcome_email" className="ml-2 block text-sm text-gray-700">
                    Send welcome email automatically
                  </label>
                </div>
                {newUserData.send_welcome_email && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Welcome email language</label>
                      <select
                        value={newUserData.welcome_email_locale}
                        onChange={(e) => setNewUserData(prev => ({ ...prev, welcome_email_locale: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        {WELCOME_EMAIL_LANGUAGES.map(lang => (
                          <option key={lang.code} value={lang.code}>{lang.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization <span className="text-gray-400 text-xs">(optional)</span></label>
                  <select
                    value={newUserData.organization_id ?? ''}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, organization_id: e.target.value ? Number(e.target.value) : null }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">No organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                  {!organizations.length && (
                    <p className="mt-1 text-xs text-gray-400">No organizations available. Create one in the Organizations tab first.</p>
                  )}
                </div>
                {newUserData.organization_id && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Organization Role</label>
                    <select
                      value={newUserData.organization_role}
                      onChange={(e) => setNewUserData(prev => ({ ...prev, organization_role: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddUser(false);
                    setNewUserData({ name: '', middle_name: '', last_name: '', email: '', password: '', role: 'user', send_welcome_email: true, welcome_email_locale: 'en', organization_id: null, organization_role: 'member' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
                  disabled={addUserLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateUser}
                  disabled={addUserLoading || !newUserData.name.trim() || !newUserData.email.trim() || newUserData.password.length < 6}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addUserLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create User
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notification Modal */}
        <NotificationModal
          isOpen={notification.isOpen}
          onClose={closeNotification}
          title={notification.title}
          message={notification.message}
          type={notification.type}
        />

        {/* Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={closeConfirmDialog}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          message={confirmDialog.message}
          variant={confirmDialog.variant || 'danger'}
          confirmText={confirmDialog.confirmText || 'Confirm'}
          cancelText={confirmDialog.cancelText || 'Cancel'}
        />
      </div>
    </AppLayout>
  );
};

export default AdminPage;
